// Client HTTP de l'API.
//
// Deux principes : le jeton n'est ajouté qu'ici, et les erreurs remontent sous une forme
// unique — un code stable venant du serveur, que l'interface traduit dans la langue de
// l'utilisateur. Le message français renvoyé par l'API ne sert qu'au diagnostic.

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? ''

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export interface PagedResult<T> {
  items: T[]
  page: number
  size: number
  totalCount: number
  totalPages: number
  hasNext: boolean
}

/** Entrée d'un catalogue de choix (unité, famille d'articles). */
export interface CatalogItem {
  id: number
  code: string
  name: string
}

/** Une famille d'articles et le comportement de suivi qu'elle impose à ses articles. */
export interface ProductTypeItem extends CatalogItem {
  trackingBehavior: 'none' | 'quantity' | 'lot'
}

/** Abonnement actif : plafonds du plan (nul = sans plafond) et usage constaté. */
export interface Subscription {
  subscriptionId: number
  planCode: string
  planName: string
  maxFarms: number | null
  maxUsers: number | null
  maxPlots: number | null
  maxAreaHectares: number | null
  farmCount: number
  userCount: number
  plotCount: number
  totalAreaHectares: number
}

export interface SignInResponse {
  accessToken: string
  expiresAt: string
  refreshToken: string
  refreshExpiresAt: string
  userId: number
  fullName: string
  email: string
  role: string
  locale: string
  organizationId: number
}

export interface SupplierProfile {
  paymentTermId: number | null
  supplierReference: string | null
  creditLimit: number | null
  /** Encours constaté : total facturé non réglé. Calculé par le serveur, jamais stocké. */
  outstanding: number
  /** Nul quand aucun plafond n'est fixé — ce qui n'est PAS zéro. */
  creditAvailable: number | null
  deliveryLeadDays: number | null
  minimumOrderAmount: number | null
  isApproved: boolean
  approvedAt: string | null
  qualityRating: number | null
  notes: string | null
}

export interface Partner {
  id: number
  publicUid: string
  /** Compteur à dix caractères, posé par la base. Jamais modifiable. */
  partnerNumber: string | null
  code: string
  name: string
  partnerKind: string
  category: string
  legalName: string | null
  firstName: string | null
  lastName: string | null
  taxId: string | null
  secondaryTaxId: string | null
  address: string | null
  city: string | null
  governorate: string | null
  postalCode: string | null
  countryCode: string
  phone: string | null
  phoneSecondary: string | null
  email: string | null
  website: string | null
  contactName: string | null
  bankName: string | null
  bankAccount: string | null
  iban: string | null
  rib: string | null
  /** Nombre de jours. Jamais négatif — la base le refuse déjà. */
  paymentTermsDays: number
  /** Encours autorisé pour ce tiers. Nul = pas de plafond, ce qui n'est PAS zéro. */
  creditLimit: number | null
  notes: string | null
  isActive: boolean
  /** Nul tant que le tiers n'a pas été qualifié fournisseur. */
  supplier: SupplierProfile | null
}

export type PartnerInput = Omit<
  Partner,
  'id' | 'publicUid' | 'partnerNumber' | 'isActive' | 'supplier' | 'name' | 'firstName' | 'lastName'
> & {
  /** Facultatif : absent, le serveur le remplit depuis la raison sociale. */
  name?: string | null
  // Prénom et nom de famille : plus aucun écran ne les envoie depuis le lot L5-02,
  // la raison sociale portant désormais la dénomination complète. Facultatifs plutôt
  // que retirés du type, pour ne pas casser un futur appelant qui les porterait
  // encore (reprise de données, mobile).
  firstName?: string | null
  lastName?: string | null
}

export type SupplierProfileInput = Partial<{
  paymentTermId: number | null
  supplierReference: string | null
  creditLimit: number | null
  deliveryLeadDays: number | null
  minimumOrderAmount: number | null
  bankName: string | null
  bankAccount: string | null
  iban: string | null
  qualityRating: number | null
  notes: string | null
  approve: boolean | null
}>

export interface Farm {
  id: number
  code: string
  name: string
  governorate: string | null
  delegation: string | null
  locality: string | null
  managerName: string | null
  phone: string | null
  totalAreaHectares: number | null
  latitude: number | null
  longitude: number | null
  // Trois colonnes posees par la migration 0050, rendues par FarmDto depuis le
  // lot L3-01 et ignorees par le client jusqu'ici.
  description: string | null
  ownerName: string | null
  address: string | null
  isActive: boolean
  /** Type d'exploitation, dans le référentiel `farm_type`. Nul s'il n'est pas renseigné. */
  farmTypeId: number | null
}

export interface Plot {
  id: number
  publicUid: string
  farmId: number
  code: string
  name: string | null
  cadastralAreaHectares: number
  soilDepthCm: number | null
  averageSlopePercent: number | null
  latitude: number | null
  longitude: number | null
  isIrrigable: boolean
  parentPlotId: number | null
  validFrom: string | null
  validTo: string | null
  isActive: boolean
  /** Référentiel `soil_type`. La parcelle le portait sans que la réponse le rende. */
  soilTypeId: number | null
  /** Référentiels `irrigation_mode` et `water_source`. Nuls en sec, ou tant qu'on ne sait pas. */
  irrigationModeId: number | null
  waterSourceId: number | null
}

export interface OrchardPlot {
  id: number
  farmId: number
  plotId: number
  cropId: number
  varietyId: number | null
  plantingYear: number
  plantingMonth: number | null
  densityPerHa: number | null
  irrigationMode: string
  certification: string
  status: string
  expectedFirstHarvestYear: number | null
  /** Zone agro-climatique de la fenêtre de récolte (spec §8.4). Nulle : aucune fenêtre appliquée. */
  region: string | null
  /** Coût de la plantation, amorti sur `usefulLifeYears`. Nul : plantation non valorisée. */
  plantationCostTotal: number | null
  /** Vie productive en années. Va avec `plantationCostTotal`. */
  usefulLifeYears: number | null
  notes: string | null
}

/**
 * Coût d'un verger pour une campagne (spec §2.10), rendu par `analytics.orchard_plot_cost`.
 * `costPerKg` est nul quand rien n'a été récolté ; `amortizedCost` est nul quand la
 * plantation n'est pas valorisée. Deux silences, jamais deux zéros.
 */
export interface OrchardPlotCost {
  orchardPlotId: number
  farmId: number
  plotId: number
  plotCode: string
  plotName: string | null
  seasonId: number
  seasonCode: string
  areaHectares: number
  interventionCost: number
  irrigationCost: number
  harvestLaborCost: number
  amortizedCost: number | null
  totalCost: number
  yieldKg: number | null
  costPerHectare: number | null
  costPerKg: number | null
}

export interface ActivityModule {
  code: 'crop' | 'orchard' | 'livestock'
  labelFr: string
  labelAr: string
  labelEn: string
}

export interface Warehouse {
  id: number
  publicUid: string
  code: string
  name: string
  farmId: number | null
  warehouseTypeId: number | null
  address: string | null
  governorate: string | null
  capacity: number | null
  capacityUnitId: number | null
  managerName: string | null
  phone: string | null
  allowNegativeStock: boolean
  isDefault: boolean
  isActive: boolean
}

export type WarehouseInput = {
  code: string
  name: string
  farmId?: number | null
  address?: string | null
  governorate?: string | null
  capacity?: number | null
  managerName?: string | null
  phone?: string | null
  allowNegativeStock?: boolean
}

/** Ce que rend un transfert : la valeur DÉPLACÉE, jamais créée. */
export interface StockTransfer {
  transferGroupUid: string
  fromWarehouseId: number
  toWarehouseId: number
  productId: number
  lotId: number | null
  quantity: number
  unitCost: number
  transferredValue: number
  remainingAtSource: number
  remainingAtTarget: number
}

export type ReferenceKind =
  | 'crop_family'
  | 'crop'
  | 'variety'
  | 'work_category'
  | 'work_type'
  | 'machine_category'
  | 'employment_type'
  // Référentiels articles. Leurs colonnes propres — comportement, symbole, dimension,
  // nature, taux — arrivent dans `ownFields`, dès la liste et pas seulement sur la fiche.
  | 'unit_of_measure'
  | 'product_type'
  | 'tracking_mode'
  | 'valuation_method'
  | 'tax_rate'
  // §14 : normalise le nom d'une culture pour que « Tomate »/« tomates »/« TOMATE » se
  // rattachent au même groupe plutôt que de disperser les campagnes.
  | 'campaign_group'
  // §7 : le classement des normes (qualité, phytosanitaire, agriculture, environnement,
  // sécurité, production). Plat, comme campaign_group : sa seule colonne propre est la
  // description.
  | 'norm_category'
  // Lot 5 : le type d'une exploitation, et ce qui décrit l'eau et le sol d'une parcelle.
  // `soil_type` existait en base depuis 0007 sans être lisible par aucune route.
  | 'farm_type'
  | 'soil_type'
  | 'irrigation_mode'
  | 'water_source'
  // Lot LV-B : les référentiels d'élevage (migration 0109) — espèces, races, catégories.
  | 'species'
  | 'breed'
  | 'animal_category'

/** Deux origines cohabitent : la ligne livrée (catalogue) et la ligne propre au client. */
export interface ReferenceItem {
  id: number
  code: string
  displayName: string
  baseName: string
  overriddenName: string | null
  isCatalog: boolean
  isActive: boolean
  isHiddenByOverride: boolean
  displayOrder: number
  /**
   * Colonnes propres au référentiel, telles que la base les porte : le comportement d'un
   * mode de suivi, le symbole et la dimension d'une unité, la nature d'une famille, le
   * taux d'une TVA.
   *
   * Portées dès la LISTE, et pas seulement sur la fiche : les demander entrée par entrée
   * coûterait vingt allers-retours pour afficher une page. Un dictionnaire plutôt que des
   * champs nommés, parce qu'elles diffèrent d'un référentiel à l'autre. Nulle pour un
   * référentiel qui n'en déclare aucune.
   */
  ownFields: Record<string, string | null> | null
}

export interface ReferenceDetail extends ReferenceItem {
  kind: ReferenceKind
  labelAr: string | null
  labelEn: string | null
  cropId: number | null
  cropName: string | null
  maturityDays: number | null
  isCertified: boolean | null
  origin: string | null
  overriddenLabelAr: string | null
  overriddenLabelEn: string | null
}

export interface ReferenceCreateInput {
  code: string
  name: string
  nameAr?: string | null
  nameEn?: string | null
  displayOrder?: number | null
  cropId?: number | null
  maturityDays?: number | null
  isCertified?: boolean
  origin?: string | null
  /**
   * Colonnes propres au référentiel, indexées par le nom que déclare son descripteur
   * côté serveur. Un champ obligatoire manquant ou une valeur hors liste fermée est
   * refusé AVANT tout accès à la base, avec un code d'erreur traduisible.
   */
  ownFields?: Record<string, string | null> | null
}

export interface ReferenceOverrideInput {
  name?: string | null
  nameAr?: string | null
  nameEn?: string | null
  isHidden?: boolean
}

interface RequestOptions {
  method?: string
  body?: unknown
  /** Corps brut (texte CSV des imports) : aucun JSON, le Content-Type est celui fourni. */
  rawBody?: string
  contentType?: string
  token?: string | null
  signal?: AbortSignal
  /** Faux sur les routes d'authentification elles-mêmes : un 401 y est une réponse, pas une session périmée. */
  retryOn401?: boolean
}

/**
 * Rafraîchisseur de session, posé par AuthProvider : appelé UNE fois quand un appel
 * authentifié reçoit 401. Il rend le nouveau jeton d'accès, ou nul quand la session
 * est finie — l'appelant purge alors la session et l'écran revient à la connexion.
 * Avant lui, un 401 en cours de route restait un message d'erreur ordinaire et la
 * session périmée persistait dans le stockage local.
 */
type SessionRefresher = () => Promise<string | null>
let sessionRefresher: SessionRefresher | null = null

export function setSessionRefresher(refresher: SessionRefresher | null): void {
  sessionRefresher = refresher
}

export interface AgroReference {
    id: number
    referenceType: string
    organizationId: number | null
    code: string
    name: string
    labelFr: string
    labelAr: string | null
    labelEn: string | null
    isActive: boolean
    isFromCatalog: boolean
    isOverridden: boolean
    overrideName: string | null
    isHidden: boolean
    displayOrder: number
    extra: string | null
}

export type AgroReferenceCreate = {
    code: string
    name: string
    labelAr?: string | null
    labelEn?: string | null
    extra?: string | null
}

export type AgroReferenceOverride = {
    name?: string | null
    labelAr?: string | null
    labelEn?: string | null
    isHidden?: boolean
}

/**
 * Un client de la plateforme, vu du panneau de super-administration.
 *
 * PLAN ET ABONNEMENT SONT NULLABLES, ET C'EST LE POINT. Les jointures de
 * `core.platform_list_clients` sont externes pour qu'un client sans abonnement apparaisse
 * dans la liste au lieu d'en disparaître : c'est précisément celui qu'il faut réparer. Le
 * type le dit, et l'écran écrit « aucun abonnement » plutôt que de laisser une case vide.
 */
export interface PlatformClient {
    organizationId: number
    legalName: string
    governorate: string | null
    isActive: boolean
    /** Horodatage ISO, rendu par <DateLisible>. */
    createdAt: string
    planCode: string | null
    planName: string | null
    subscriptionStatus: string | null
    farmCount: number
    userCount: number
}

/**
 * Un plafond de la formule visée que l'usage actuel dépasse DÉJÀ, et de combien.
 *
 * LES CHIFFRES ARRIVENT CALCULÉS, et l'écran n'en refait aucun. `PlanDowngrade.Overruns`
 * vit dans le domaine .NET et porte ses propres tests, dont celui du plafond nul — un
 * plafond nul signifie « sans limite », jamais zéro. Refaire la comparaison en TypeScript
 * en donnerait une seconde version que rien n'éprouve : `src/Web` n'a aucune bibliothèque
 * de test de composant.
 */
export interface PlanOverrun {
    /** `farms`, `plots`, `area` ou `users` — un code stable que l'écran traduit. */
    limit: string
    current: number
    allowed: number
    excess: number
    /**
     * Ce plafond refuse-t-il vraiment une création ? FAUX pour les utilisateurs :
     * `CreateUser` n'appelle aucune garde. L'écran doit le dire plutôt que de laisser
     * croire à une limite.
     */
    isEnforced: boolean
}

/**
 * Le cycle de vie d'un abonnement, tel que la fiche l'affiche (migration 0077). `graceEndsOn`
 * est le jour où la lecture seule commencera si rien n'est payé — nul hors paiement attendu.
 */
export interface ClientLifecycle {
  status: string
  currentPeriodEnd: string | null
  trialEndsOn: string | null
  autoRenew: boolean
  pastDueSince: string | null
  graceEndsOn: string | null
  pendingPlanCode: string | null
  pendingPlanName: string | null
  pendingChangeOn: string | null
  cancelAtPeriodEnd: boolean
  cancelReason: string | null
}

/** Une ligne de l'historique. Les états sont le JSON que la base a photographié. */
export interface SubscriptionEvent {
  occurredAt: string
  eventType: string
  source: 'platform' | 'job'
  reason: string | null
  previousState: string | null
  newState: string
}

export interface BillingPolicy {
  graceDays: number
  reminderDays: number[]
}

/** Un avis au client : son genre, la date dont il parle, et à combien de jours pour un rappel. */
export interface SubscriptionNotice {
  id: number
  kind: 'renewal_reminder' | 'trial_ending' | 'past_due' | 'suspended' | 'expired' | 'cancelled'
  referenceDate: string
  daysBefore: number | null
  createdAt: string
}

/**
 * Une formule telle que le panneau l'administre (migration 0076). Les plafonds NULS veulent
 * dire « sans limite » — jamais zéro, que la base refuse.
 */
export interface PlanAdmin {
  id: number
  code: string
  name: string
  nameAr: string | null
  nameEn: string | null
  description: string | null
  billingPeriod: 'monthly' | 'yearly'
  price: number
  currencyCode: string
  pricePerExtraFarm: number | null
  limits: { maxFarms: number | null; maxPlots: number | null; maxUsers: number | null; maxAreaHectares: number | null }
  trialDays: number
  isPublic: boolean
  isActive: boolean
  displayOrder: number
  includedModuleCount: number | null
  modules: string[]
  features: string[]
  liveSubscriptions: number
  rowVersion: number
}

/** Ce qu'on envoie pour créer ou modifier une formule. Le code n'est lu qu'à la création. */
export interface PlanPayload {
  code: string | null
  name: string
  nameAr: string | null
  nameEn: string | null
  description: string | null
  billingPeriod: 'monthly' | 'yearly'
  price: number
  pricePerExtraFarm: number | null
  maxFarms: number | null
  maxUsers: number | null
  maxPlots: number | null
  maxAreaHectares: number | null
  trialDays: number
  isPublic: boolean
  displayOrder: number
  includedModuleCount: number | null
  modules: string[]
  features: string[]
  rowVersion: number | null
}

/** Une fonctionnalité du catalogue. Chacune garde des routes de l'API. */
export interface FeatureInfo {
  code: string
  name: string
  nameAr: string | null
  nameEn: string | null
  displayOrder: number
}

/**
 * Une fonctionnalité chez un client. `included` est la réponse de la même règle que celle
 * qui refuse les routes au client (EntitlementResolver) ; `source` dit qui décide
 * aujourd'hui. Une exception échue reste listée — `overrideIncluded` renseigné — mais
 * `source` redevient « plan ».
 */
export interface ClientFeature {
  code: string
  inPlan: boolean
  included: boolean
  source: 'plan' | 'override'
  overrideIncluded: boolean | null
  overrideReason: string | null
  overrideStartsOn: string | null
  overrideExpiresOn: string | null
}

/** Une formule du catalogue, et ce que le basculement coûterait à CE client. */
export interface PlanOffer {
    code: string
    name: string
    /** Noms de `saas.plan.labels` : une formule créée au panneau n'a pas de clé de dictionnaire. */
    nameAr?: string | null
    nameEn?: string | null
    price: number
    billingPeriod: string
    currencyCode: string
    maxFarms: number | null
    maxPlots: number | null
    maxUsers: number | null
    maxAreaHectares: number | null
    /** Vide quand cette formule ne retire rien au client. */
    overruns: PlanOverrun[]
}

/** L'abonnement d'un client, avec les plafonds de sa formule. */
export interface ClientSubscription {
    subscriptionId: number
    status: string
    planCode: string | null
    planName: string | null
    billingPeriod: string | null
    price: number | null
    currencyCode: string | null
    /** Dates ISO (jour seul), rendues par <DateLisible>. */
    startedOn: string | null
    currentPeriodEnd: string | null
    endedOn: string | null
    cancellationReason: string | null
    autoRenew: boolean
    maxFarms: number | null
    maxPlots: number | null
    maxUsers: number | null
    maxAreaHectares: number | null
}

/** Une activité chez un client, et ce que la fermer ferait vraiment. */
export interface ClientModule {
    moduleCode: string
    isEnabled: boolean
    openedAt: string | null
    closedAt: string | null
    /**
     * Fermer ce module refuse-t-il des écrans, ou seulement une entrée de menu ? Seul
     * `crop` répond vrai aujourd'hui : `RequireModule` n'est posé que sur ses routes, et
     * l'arboriculture comme l'élevage n'existent pas encore dans le produit.
     */
    guardsRoutes: boolean
}

/** Ce que le client consomme, compté en direct et non lu d'un compteur. */
export interface ClientUsage {
    farmCount: number
    plotCount: number
    userCount: number
    totalAreaHectares: number
}

/**
 * La fiche d'un client.
 *
 * `subscription` EST NUL EN BLOC quand le client n'en a pas : les jointures de
 * `core.platform_client_detail` sont externes pour qu'un client sans abonnement apparaisse
 * quand même — c'est précisément celui qu'il faut réparer.
 */
export interface PlatformClientDetail {
    organizationId: number
    legalName: string
    orgType: string | null
    taxId: string | null
    city: string | null
    governorate: string | null
    phone: string | null
    email: string | null
    isActive: boolean
    createdAt: string
    subscription: ClientSubscription | null
    modules: ClientModule[]
    usage: ClientUsage
    plans: PlanOffer[]
}

/**
 * Ce que le formulaire de création envoie.
 *
 * `adminPassword` est le mot de passe INITIAL, et il ne vit que le temps de cet aller : le
 * gestionnaire .NET le hache et ne transmet qu'une empreinte à la base. Rien n'en est
 * conservé côté navigateur.
 */
export interface NewPlatformClient {
    legalName: string
    governorate: string
    email: string
    adminName: string
    adminEmail: string
    adminPassword: string
    planCode: string
    modules: string[]
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, rawBody, contentType = 'application/json', token, signal, retryOn401 = true } = options

  const response = await send(path, { method, body, rawBody, contentType, token, signal })

  // Jeton d'accès périmé : une rotation, puis le même appel rejoué avec le nouveau jeton.
  if (response.status === 401 && token && retryOn401 && sessionRefresher !== null) {
    const fresh = await sessionRefresher()
    if (fresh !== null) {
      return read<T>(await send(path, { method, body, rawBody, contentType, token: fresh, signal }))
    }
  }

  return read<T>(response)
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const { method = 'GET', body, rawBody, contentType = 'application/json', token, signal } = options

  const headers: Record<string, string> = { Accept: 'application/json' }

  if (body !== undefined || rawBody !== undefined) {
    headers['Content-Type'] = contentType
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  let response: Response

  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: rawBody !== undefined ? rawBody : body === undefined ? undefined : JSON.stringify(body),
      signal,
    })
  } catch (cause) {
    // fetch ne rejette que sur une panne réseau : distinguer ce cas évite d'afficher
    // « erreur inattendue » quand le serveur est simplement injoignable.
    throw new ApiError(0, 'error.network', String(cause))
  }

  return response
}

async function read<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  const payload: unknown = text.length > 0 ? JSON.parse(text) : null

  if (!response.ok) {
    const problem = payload as { code?: string; detail?: string; title?: string } | null

    throw new ApiError(
      response.status,
      problem?.code,
      problem?.detail ?? problem?.title ?? response.statusText,
    )
  }

  return payload as T
}

export const api = {
  signIn: (email: string, password: string) =>
    request<SignInResponse>('/api/v1/auth/sign-in', { method: 'POST', body: { email, password } }),

  // Rotation du jeton : le jeton d'accès est court, la session vit par le jeton de
  // rafraîchissement, que la déconnexion révoque côté serveur.
  refreshSession: (refreshToken: string) =>
    request<SignInResponse>('/api/v1/auth/refresh', { method: 'POST', body: { refreshToken }, retryOn401: false }),

  // Réinitialisation du mot de passe, hors session. Le serveur répond la même chose que
  // l'adresse corresponde à un compte ou non. `token` n'est rempli qu'en développement :
  // aucun écran ne doit ni l'afficher ni s'en servir — le lien voyage par le courriel.
  requestPasswordReset: (email: string) =>
    request<{ accepted: boolean; token?: string | null; expiresAt?: string | null }>(
      '/api/v1/auth/password-reset', { method: 'POST', body: { email }, retryOn401: false }),

  // Le jeton est celui du paramètre `jeton` du lien reçu par courriel. 204 sans corps.
  confirmPasswordReset: (token: string, newPassword: string) =>
    request<void>('/api/v1/auth/password-reset/confirm', {
      method: 'POST', body: { token, newPassword }, retryOn401: false }),

  signOut: (refreshToken: string) =>
    request<void>('/api/v1/auth/sign-out', { method: 'POST', body: { refreshToken }, retryOn401: false }),

  // Changer son propre mot de passe, en étant connecté. Le jeton de rafraîchissement
  // désigne la session COURANTE, celle que le serveur épargne : toutes les autres sont
  // révoquées. Sans lui, elles tomberaient toutes, celle-ci comprise.
  //
  // PAS DE REJEU APRÈS 401, et ce n'est pas une économie. Le rejeu fait d'abord TOURNER le
  // jeton de rafraîchissement ; le corps déjà composé porterait alors un jeton périmé, que
  // le serveur ne reconnaîtrait plus comme la session courante — il la révoquerait avec les
  // autres, et l'utilisateur se retrouverait déconnecté pour avoir changé son mot de passe.
  // Ici un 401 veut dire « mot de passe actuel faux », et c'est ce que l'écran doit dire.
  changePassword: (token: string, currentPassword: string, newPassword: string, refreshToken: string) =>
    request<void>('/api/v1/auth/password', {
      method: 'POST',
      token,
      body: { currentPassword, newPassword, refreshToken },
      retryOn401: false,
    }),

  listModules: (token: string, signal?: AbortSignal) =>
    request<ActivityModule[]>('/api/v1/modules', { token, signal }),

  // Nul quand l'entreprise n'a pas d'abonnement actif : c'est un état, pas une erreur.
  getSubscription: (token: string, signal?: AbortSignal) =>
    request<Subscription | null>('/api/v1/subscription', { token, signal }),

  // Catalogues de choix : ce que les sélecteurs affichent à la place d'un identifiant brut.
  listUnits: (token: string, signal?: AbortSignal) =>
    request<CatalogItem[]>('/api/v1/catalog/units', { token, signal }),

  listProductTypes: (token: string, signal?: AbortSignal) =>
    request<ProductTypeItem[]>('/api/v1/catalog/product-types', { token, signal }),

  listFarms: (
    token: string,
    params: { term?: string; page?: number; includeInactive?: boolean },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.term) query.set('term', params.term)
    if (params.page) query.set('page', String(params.page))
    // Explicite et non « si vrai » : une exploitation brouillon est inactive, et
    // c'est precisement celle que l'utilisateur cherche apres l'avoir creee.
    if (params.includeInactive) query.set('includeInactive', 'true')

    return request<PagedResult<Farm>>(`/api/v1/farms?${query.toString()}`, { token, signal })
  },

  listPlots: (token: string, params: { farmId?: number; term?: string; page?: number }, signal?: AbortSignal) => {
    const query = new URLSearchParams()
    if (params.farmId) query.set('farmId', String(params.farmId))
    if (params.term) query.set('term', params.term)
    if (params.page) query.set('page', String(params.page))

    return request<PagedResult<Plot>>(`/api/v1/plots?${query.toString()}`, { token, signal })
  },

  listOrchardPlots: (
    token: string,
    params: { farmId?: number; term?: string; page?: number },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.farmId) query.set('farmId', String(params.farmId))
    if (params.term) query.set('term', params.term)
    if (params.page) query.set('page', String(params.page))

    return request<PagedResult<OrchardPlot>>(`/api/v1/orchard/plots?${query.toString()}`, { token, signal })
  },

  createOrchardPlot: (token: string, body: OrchardPlotInput) =>
    request<OrchardPlot>('/api/v1/orchard/plots', { method: 'POST', token, body }),

  /** Les choix des formulaires du verger, d'un coup et sans pagination. */
  getOrchardCatalog: (token: string, signal?: AbortSignal) =>
    request<OrchardCatalog>('/api/v1/orchard/catalog', { token, signal }),

  getOrchardPlot: (token: string, id: number, signal?: AbortSignal) =>
    request<OrchardPlot>(`/api/v1/orchard/plots/${id}`, { token, signal }),

  listOrchardInterventions: (
    token: string,
    params: { farmId?: number; orchardPlotId?: number; term?: string; page?: number },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.farmId) query.set('farmId', String(params.farmId))
    if (params.orchardPlotId) query.set('orchardPlotId', String(params.orchardPlotId))
    if (params.term) query.set('term', params.term)
    if (params.page) query.set('page', String(params.page))

    return request<PagedResult<OrchardIntervention>>(`/api/v1/orchard/interventions?${query.toString()}`, { token, signal })
  },

  createOrchardIntervention: (token: string, body: OrchardInterventionInput) =>
    request<OrchardIntervention>('/api/v1/orchard/interventions', { method: 'POST', token, body }),

  validateOrchardIntervention: (token: string, id: number) =>
    request<OrchardIntervention>(`/api/v1/orchard/interventions/${id}/validate`, { method: 'POST', token }),

  listOrchardHarvestPasses: (
    token: string,
    params: { farmId?: number; orchardPlotId?: number; seasonId?: number; term?: string; page?: number },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.farmId) query.set('farmId', String(params.farmId))
    if (params.orchardPlotId) query.set('orchardPlotId', String(params.orchardPlotId))
    if (params.seasonId) query.set('seasonId', String(params.seasonId))
    if (params.term) query.set('term', params.term)
    if (params.page) query.set('page', String(params.page))

    return request<PagedResult<OrchardHarvestPass>>(`/api/v1/orchard/harvest-passes?${query.toString()}`, { token, signal })
  },

  createOrchardHarvestPass: (token: string, body: OrchardHarvestPassInput) =>
    request<OrchardHarvestPass>('/api/v1/orchard/harvest-passes', { method: 'POST', token, body }),

  listOrchardQualityControls: (
    token: string,
    params: { farmId?: number; harvestPassId?: number; page?: number },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.farmId) query.set('farmId', String(params.farmId))
    if (params.harvestPassId) query.set('harvestPassId', String(params.harvestPassId))
    if (params.page) query.set('page', String(params.page))

    return request<PagedResult<OrchardQualityControl>>(`/api/v1/orchard/quality-controls?${query.toString()}`, { token, signal })
  },

  createOrchardQualityControl: (token: string, body: OrchardQualityControlInput) =>
    request<OrchardQualityControl>('/api/v1/orchard/quality-controls', { method: 'POST', token, body }),

  listOrchardIrrigations: (
    token: string,
    params: { farmId?: number; orchardPlotId?: number; term?: string; page?: number },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.farmId) query.set('farmId', String(params.farmId))
    if (params.orchardPlotId) query.set('orchardPlotId', String(params.orchardPlotId))
    if (params.term) query.set('term', params.term)
    if (params.page) query.set('page', String(params.page))

    return request<PagedResult<OrchardIrrigation>>(`/api/v1/orchard/irrigations?${query.toString()}`, { token, signal })
  },

  createOrchardIrrigation: (token: string, body: OrchardIrrigationInput) =>
    request<OrchardIrrigation>('/api/v1/orchard/irrigations', { method: 'POST', token, body }),

  listOrchardTransformations: (
    token: string,
    params: { farmId?: number; harvestPassId?: number; term?: string; page?: number },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.farmId) query.set('farmId', String(params.farmId))
    if (params.harvestPassId) query.set('harvestPassId', String(params.harvestPassId))
    if (params.term) query.set('term', params.term)
    if (params.page) query.set('page', String(params.page))

    return request<PagedResult<OrchardTransformation>>(`/api/v1/orchard/transformations?${query.toString()}`, { token, signal })
  },

  createOrchardTransformation: (token: string, body: OrchardTransformationInput) =>
    request<OrchardTransformation>('/api/v1/orchard/transformations', { method: 'POST', token, body }),

  listOrchardPhenologies: (
    token: string,
    params: { farmId?: number; orchardPlotId?: number; term?: string; page?: number },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.farmId) query.set('farmId', String(params.farmId))
    if (params.orchardPlotId) query.set('orchardPlotId', String(params.orchardPlotId))
    if (params.term) query.set('term', params.term)
    if (params.page) query.set('page', String(params.page))

    return request<PagedResult<OrchardPhenology>>(`/api/v1/orchard/phenologies?${query.toString()}`, { token, signal })
  },

  createOrchardPhenology: (token: string, body: OrchardPhenologyInput) =>
    request<OrchardPhenology>('/api/v1/orchard/phenologies', { method: 'POST', token, body }),

  // Le rapport de coût du verger n'est pas paginé : une ligne par verger et par
  // campagne. Sans campagne désignée, toutes les campagnes sont rendues.
  listOrchardPlotCosts: (
    token: string,
    params: { farmId?: number; seasonId?: number },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.farmId) query.set('farmId', String(params.farmId))
    if (params.seasonId) query.set('seasonId', String(params.seasonId))

    return request<OrchardPlotCost[]>(`/api/v1/orchard/reports/plot-cost?${query.toString()}`, { token, signal })
  },

  listPartners: (
    token: string,
    params: { term?: string; kind?: string; page?: number; sort?: string; includeInactive?: boolean },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.term) query.set('term', params.term)
    if (params.kind) query.set('kind', params.kind)
    if (params.page) query.set('page', String(params.page))
    if (params.sort) query.set('sort', params.sort)
    if (params.includeInactive) query.set('includeInactive', 'true')

    return request<PagedResult<Partner>>(`/api/v1/partners?${query.toString()}`, { token, signal })
  },

  createPartner: (token: string, body: PartnerInput) =>
    request<Partner>('/api/v1/partners', { method: 'POST', token, body }),

  // Le code proposé pour la prochaine fiche. `code: null` veut dire « à vous de
  // saisir », pas « erreur ».
  nextPartnerCode: (token: string, kind: 'supplier' | 'customer', signal?: AbortSignal) =>
    request<{ code: string | null }>(`/api/v1/partners/next-code?kind=${kind}`, { token, signal }),

  nextProductCode: (token: string, signal?: AbortSignal) =>
    request<{ code: string | null }>('/api/v1/products/next-code', { token, signal }),

  updatePartner: (token: string, id: number, body: PartnerInput) =>
    request<Partner>(`/api/v1/partners/${id}`, { method: 'PUT', token, body }),

  setPartnerActivation: (token: string, id: number, active: boolean) =>
    request<Partner>(`/api/v1/partners/${id}/activation`, { method: 'POST', token, body: { active } }),

  getPartner: (token: string, id: number, signal?: AbortSignal) =>
    request<Partner>(`/api/v1/partners/${id}`, { token, signal }),

  saveSupplierProfile: (token: string, id: number, body: SupplierProfileInput) =>
    request<Partner>(`/api/v1/partners/${id}/supplier`, { method: 'PUT', token, body }),

  listWarehouses: (
    token: string,
    params: { term?: string; farmId?: number; includeInactive?: boolean; page?: number },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.term) query.set('term', params.term)
    if (params.farmId) query.set('farmId', String(params.farmId))
    if (params.includeInactive) query.set('includeInactive', 'true')
    if (params.page) query.set('page', String(params.page))

    return request<PagedResult<Warehouse>>(`/api/v1/warehouses?${query.toString()}`, { token, signal })
  },

  createWarehouse: (token: string, body: WarehouseInput) =>
    request<Warehouse>('/api/v1/warehouses', { method: 'POST', token, body }),

  // farmId nul DÉTACHE le dépôt : c'est pour cela que le point de terminaison est un
  // PUT et non un PATCH — le rattachement est remplacé en entier, y compris par « aucun ».
  attachWarehouseToFarm: (token: string, id: number, farmId: number | null) =>
    request<Warehouse>(`/api/v1/warehouses/${id}/farm`, { method: 'PUT', token, body: { farmId } }),

  transferStock: (
    token: string,
    body: {
      fromWarehouseId: number
      toWarehouseId: number
      productId: number
      quantity: number
      /** Obligatoire pour un article suivi par lot : c'est un lot qui change de dépôt. */
      lotId?: number | null
      notes?: string | null
    },
  ) => request<StockTransfer>('/api/v1/stock/transfers', { method: 'POST', token, body }),

  listReferences: (
    token: string,
    kind: ReferenceKind,
    params: { term?: string; includeInactive?: boolean },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.term) query.set('term', params.term)
    if (params.includeInactive) query.set('includeInactive', 'true')

    return request<ReferenceItem[]>(`/api/v1/references/${kind}?${query.toString()}`, { token, signal })
  },

  getReference: (token: string, kind: ReferenceKind, id: number, signal?: AbortSignal) =>
    request<ReferenceDetail>(`/api/v1/references/${kind}/${id}`, { token, signal }),

  createReference: (token: string, kind: ReferenceKind, body: ReferenceCreateInput) =>
    request<ReferenceDetail>(`/api/v1/references/${kind}`, { method: 'POST', token, body }),

  setReferenceActivation: (token: string, kind: ReferenceKind, id: number, isActive: boolean) =>
    request<ReferenceDetail>(`/api/v1/references/${kind}/${id}/activation`, {
      method: 'PUT',
      token,
      body: { isActive },
    }),

  setReferenceOverride: (token: string, kind: ReferenceKind, id: number, body: ReferenceOverrideInput) =>
    request<ReferenceDetail>(`/api/v1/references/${kind}/${id}/override`, {
      method: 'PUT',
      token,
      body,
    }),

  listStockBalances: (
    token: string,
    params: { warehouseId?: number; productId?: number },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.warehouseId) query.set('warehouseId', String(params.warehouseId))
    if (params.productId) query.set('productId', String(params.productId))

    return request<StockBalancesResult>(`/api/v1/stock/balances?${query.toString()}`, { token, signal })
  },

  listMachines: (token: string, signal?: AbortSignal) =>
    request<MachineItem[]>('/api/v1/machines', { token, signal }),

  /**
   * Les salariés qu'on DÉSIGNE sur une intervention : aucun montant. C'est la lecture
   * que tout utilisateur qui saisit reçoit ; ce que coûte un salarié ne descend que par
   * `listEmployeesWithCosts`.
   */
  listEmployees: (token: string, signal?: AbortSignal) =>
    request<EmployeeItem[]>('/api/v1/employees', { token, signal }),

  /** Réservée à qui voit les coûts : le serveur répond 403 aux autres. Inactifs compris. */
  listEmployeesWithCosts: (token: string, signal?: AbortSignal) =>
    request<EmployeeWithCosts[]>('/api/v1/employees/with-costs', { token, signal }),

  createEmployee: (token: string, body: EmployeeInput) =>
    request<{ id: number }>('/api/v1/employees', { method: 'POST', token, body }),

  updateEmployee: (token: string, employeeId: number, body: EmployeeInput) =>
    request<{ id: number }>(`/api/v1/employees/${employeeId}`, { method: 'PUT', token, body }),

  /** Une bête du registre : son identité, pas son histoire. */
  listAnimals: (token: string, params: { farmId?: number; species?: string }, signal?: AbortSignal) => {
    const query = new URLSearchParams()
    if (params.farmId) query.set('farmId', String(params.farmId))
    if (params.species) query.set('species', params.species)

    return request<AnimalItem[]>(`/api/v1/livestock/animals?${query.toString()}`, { token, signal })
  },

  registerAnimal: (token: string, body: AnimalInput) =>
    request<{ id: number }>('/api/v1/livestock/animals', { method: 'POST', token, body }),

  recordHerdMovement: (token: string, animalId: number, body: HerdMovementInput) =>
    request<{ id: number }>(`/api/v1/livestock/animals/${animalId}/movements`, { method: 'POST', token, body }),

  /** Le troupeau présent, compté par espèce et catégorie. */
  herdInventory: (token: string, params: { farmId?: number }, signal?: AbortSignal) => {
    const query = new URLSearchParams()
    if (params.farmId) query.set('farmId', String(params.farmId))

    return request<HerdInventoryLine[]>(`/api/v1/livestock/inventory?${query.toString()}`, { token, signal })
  },

  /** Les interventions sanitaires d'une bête, des plus récentes aux plus anciennes. */
  listHealthEvents: (token: string, animalId: number, signal?: AbortSignal) =>
    request<HealthEventItem[]>(`/api/v1/livestock/health-events?animalId=${animalId}`, { token, signal }),

  recordHealthEvent: (token: string, animalId: number, body: HealthEventInput) =>
    request<{ id: number }>(`/api/v1/livestock/animals/${animalId}/health-events`, { method: 'POST', token, body }),

  /** Les bêtes présentes sans vaccination récente : l'alerte « vaccination due ». */
  vaccinationAlerts: (token: string, params: { farmId?: number; species?: string }, signal?: AbortSignal) => {
    const query = new URLSearchParams()
    if (params.farmId) query.set('farmId', String(params.farmId))
    if (params.species) query.set('species', params.species)

    return request<VaccinationDueItem[]>(`/api/v1/livestock/vaccination-alerts?${query.toString()}`, { token, signal })
  },

  /** Les traites d'une bête, des plus récentes aux plus anciennes. */
  listProductions: (token: string, animalId: number, signal?: AbortSignal) =>
    request<ProductionItem[]>(`/api/v1/livestock/productions?animalId=${animalId}`, { token, signal }),

  recordProduction: (token: string, animalId: number, body: ProductionInput) =>
    request<{ id: number }>(`/api/v1/livestock/animals/${animalId}/productions`, { method: 'POST', token, body }),

  /** Le total trait un jour donné : la somme des traites constatées ce jour-là. */
  productionDailyTotal: (token: string, animalId: number, productionDate: string, signal?: AbortSignal) =>
    request<number>(
      `/api/v1/livestock/productions/daily-total?animalId=${animalId}&productionDate=${productionDate}`,
      { token, signal },
    ),

  /** Les alimentations d'une bête : jour, produit, quantité. */
  listFeedingRecords: (token: string, animalId: number, signal?: AbortSignal) =>
    request<FeedingRecordItem[]>(`/api/v1/livestock/feeding-records?animalId=${animalId}`, { token, signal }),

  recordFeeding: (token: string, animalId: number, body: FeedingInput) =>
    request<{ id: number }>(`/api/v1/livestock/animals/${animalId}/feedings`, { method: 'POST', token, body }),

  /** Les saillies d'une bête : jour, type, date prévue. */
  listReproductions: (token: string, animalId: number, signal?: AbortSignal) =>
    request<ReproductionEventItem[]>(`/api/v1/livestock/reproductions?animalId=${animalId}`, { token, signal }),

  recordReproduction: (token: string, animalId: number, body: ReproductionInput) =>
    request<{ id: number }>(`/api/v1/livestock/animals/${animalId}/reproductions`, { method: 'POST', token, body }),

  /** Réservé à qui voit les coûts : le coût par tête et par litre, et la marge de chaque bête. */
  livestockCostReport: (token: string, params: { farmId?: number }, signal?: AbortSignal) => {
    const query = new URLSearchParams()
    if (params.farmId) query.set('farmId', String(params.farmId))

    return request<LivestockCostReport>(`/api/v1/livestock/cost-report?${query.toString()}`, { token, signal })
  },

  /** La campagne ouverte de la parcelle porte l'objet de coût que l'intervention impute. */
  getPlotCostObject: (token: string, plotId: number, signal?: AbortSignal) =>
    request<PlotCostObject>(`/api/v1/plots/${plotId}/cost-object`, { token, signal }),

  listStockedProducts: (token: string, warehouseId: number, signal?: AbortSignal) =>
    request<StockedProduct[]>(`/api/v1/stock/products?warehouseId=${warehouseId}`, { token, signal }),

  createOperation: (token: string, body: OperationInput) =>
    request<OperationSummary>('/api/v1/operations', { method: 'POST', token, body }),

  /** Après validation, l'intervention est immuable : c'est le seul bouton qui l'y conduit. */
  validateOperation: (token: string, operationId: number) =>
    request<OperationSummary>(`/api/v1/operations/${operationId}/validate`, { method: 'POST', token }),

  consumeForOperation: (
    token: string,
    operationId: number,
    body: {
      warehouseId: number
      lines: { productId: number; quantity: number; unitOfMeasureId: number }[]
      movementDate?: string | null
    },
  ) =>
    request<ConsumeForOperationResult>(`/api/v1/operations/${operationId}/consumptions`, {
      method: 'POST',
      token,
      body,
    }),

  /**
   * Le prévu et le réel des intrants, article par article. Des quantités, aucun montant :
   * la lecture est ouverte à quiconque lit l'intervention.
   */
  getOperationInputPlan: (token: string, operationId: number, signal?: AbortSignal) =>
    request<OperationInputPlanLine[]>(`/api/v1/operations/${operationId}/input-plan`, { token, signal }),

  getOperationCostBreakdown: (token: string, operationId: number, signal?: AbortSignal) =>
    request<OperationCostBreakdown>(`/api/v1/operations/${operationId}/cost-breakdown`, { token, signal }),

  /** Le corps EST le fichier CSV ; le rapport revient en 200 même quand des lignes sont refusées. */
  importReferencesCsv: (token: string, kind: ReferenceKind, csvText: string) =>
    request<ImportReport>(`/api/v1/references/${kind}/import`, {
      method: 'POST',
      token,
      rawBody: csvText,
      contentType: 'text/csv; charset=utf-8',
    }),

  importOperationsCsv: (token: string, csvText: string, validate = true) =>
    request<ImportReport>(`/api/v1/operations/import?validate=${validate ? 'true' : 'false'}`, {
      method: 'POST',
      token,
      rawBody: csvText,
      contentType: 'text/csv; charset=utf-8',
    }),

  // Exploitations - fiche / création
  getFarm: (token: string, id: number, signal?: AbortSignal) =>
    request<Farm>(`/api/v1/farms/${id}`, { token, signal }),

  createFarm: (token: string, body: FarmInput) =>
    request<Farm>('/api/v1/farms', { method: 'POST', token, body }),

  /** Dit, ou retire (nul), le type d'une exploitation existante. */
  setFarmType: (token: string, farmId: number, farmTypeId: number | null) =>
    request<Farm>(`/api/v1/farms/${farmId}/type`, { method: 'PUT', token, body: { farmTypeId } }),

  // Parcelles - fiche / création
  getPlot: (token: string, id: number, signal?: AbortSignal) =>
    request<Plot>(`/api/v1/plots/${id}`, { token, signal }),

  createPlot: (token: string, body: PlotInput) =>
    request<Plot>('/api/v1/plots', { method: 'POST', token, body }),

  /**
   * Irrigable, mode d'irrigation et source d'eau : LES TROIS D'UN SEUL GESTE. Une parcelle
   * en sec n'a ni mode ni source, et le serveur refuse ce qui se contredit.
   */
  setPlotIrrigation: (
    token: string,
    plotId: number,
    body: { isIrrigable: boolean; irrigationModeId: number | null; waterSourceId: number | null },
  ) => request<Plot>(`/api/v1/plots/${plotId}/irrigation`, { method: 'PUT', token, body }),

  // Campagnes / Assolement
  listSeasons: (token: string, signal?: AbortSignal) =>
    request<Season[]>(`/api/v1/seasons`, { token, signal }).catch(() => [] as Season[]),

  /** Ce que les clients doivent, rangé par ancienneté d'échéance au jour dit (aujourd'hui par défaut). */
  getCustomerReceivables: (token: string, asOf?: string | null, signal?: AbortSignal) =>
    request<Receivables>(`/api/v1/customer-receivables${asOf ? `?asOf=${asOf}` : ''}`, { token, signal }),

  /** Les factures non soldées d'un client, et ses avances à imputer. */
  getPartnerReceivable: (token: string, partnerId: number, signal?: AbortSignal) =>
    request<PartnerReceivableDetail>(`/api/v1/customer-receivables/${partnerId}`, { token, signal }),

  listCustomerPayments: (token: string, partnerId?: number | null, signal?: AbortSignal) =>
    request<CustomerPayment[]>(`/api/v1/customer-payments${partnerId ? `?partnerId=${partnerId}` : ''}`, { token, signal }),

  listCustomerPaymentMethods: (token: string, signal?: AbortSignal) =>
    request<CustomerPaymentMethod[]>('/api/v1/customer-payments/methods', { token, signal }),

  recordCustomerPayment: (token: string, body: CustomerPaymentInput) =>
    request<CustomerPayment>('/api/v1/customer-payments', { method: 'POST', token, body }),

  /** Impute tout ou partie de l'avance d'un encaissement. */
  allocateCustomerPayment: (token: string, id: number, allocations: CustomerPaymentAllocation[]) =>
    request<CustomerPayment>(`/api/v1/customer-payments/${id}/allocations`, { method: 'POST', token, body: { allocations } }),

  /** Le motif est obligatoire : il distingue le chèque impayé de l'erreur de saisie. */
  cancelCustomerPayment: (token: string, id: number, reason: string) =>
    request<CustomerPayment>(`/api/v1/customer-payments/${id}/cancel`, { method: 'POST', token, body: { reason } }),

  /** Fixe, déplace ou — avec `null` — retire l'échéance d'une facture émise. */
  setInvoiceDueDate: (token: string, invoiceId: number, dueDate: string | null) =>
    request<InvoiceBalance>(`/api/v1/customer-invoices/${invoiceId}/due-date`, { method: 'PUT', token, body: { dueDate } }),

  /** Le budget d'une campagne, culture par culture — budgétées ou encore à budgéter. */
  getSeasonBudget: (token: string, seasonId: number, signal?: AbortSignal) =>
    request<SeasonBudget>(`/api/v1/seasons/${seasonId}/budgets`, { token, signal }),

  /** « Prévisionnel ↔ Réalisé ↔ Écart ↔ % écart » : calculé par le serveur, jamais ici. */
  getBudgetComparison: (token: string, seasonId: number, signal?: AbortSignal) =>
    request<BudgetComparison>(`/api/v1/seasons/${seasonId}/budgets/comparison`, { token, signal }),

  /** Pose ou révise les hypothèses de vente d'une culture ; la budgète si elle ne l'était pas. */
  setCropBudget: (token: string, seasonId: number, cropId: number, body: CropBudgetAssumptionsInput) =>
    request<CropBudget>(`/api/v1/seasons/${seasonId}/budgets/${cropId}`, { method: 'PUT', token, body }),

  addBudgetLine: (token: string, seasonId: number, cropId: number, body: BudgetLineInput) =>
    request<CropBudget>(`/api/v1/seasons/${seasonId}/budgets/${cropId}/lines`, { method: 'POST', token, body }),

  removeBudgetLine: (token: string, seasonId: number, cropId: number, lineId: number) =>
    request<CropBudget>(`/api/v1/seasons/${seasonId}/budgets/${cropId}/lines/${lineId}`, { method: 'DELETE', token }),

  /** La culture redevient « non budgétée » — ce qui n'est pas « budgétée à zéro ». */
  removeCropBudget: (token: string, seasonId: number, cropId: number) =>
    request<void>(`/api/v1/seasons/${seasonId}/budgets/${cropId}`, { method: 'DELETE', token }),

  listCroppingPlan: (token: string, params: { seasonId?: number; farmId?: number; plotId?: number }, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    if (params.seasonId) q.set('seasonId', String(params.seasonId))
    if (params.farmId) q.set('farmId', String(params.farmId))
    if (params.plotId) q.set('plotId', String(params.plotId))
    return request<PlotSeason[]>(`/api/v1/cropping-plan?${q.toString()}`, { token, signal })
  },

  assignCrop: (token: string, body: AssignCropInput) =>
    request<PlotSeason>('/api/v1/cropping-plan', { method: 'POST', token, body }),

  getDoubleCropping: (token: string, signal?: AbortSignal) =>
    request<{ enabled: boolean }>('/api/v1/cropping-plan/double-cropping', { token, signal }),

  // Normes (§8) et leur application aux campagnes (§13)
  listNorms: (
    token: string,
    params: { normCategoryId?: number; term?: string; includeInactive?: boolean },
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams()
    if (params.normCategoryId) q.set('normCategoryId', String(params.normCategoryId))
    if (params.term) q.set('term', params.term)
    if (params.includeInactive) q.set('includeInactive', 'true')
    return request<Norm[]>(`/api/v1/norms?${q.toString()}`, { token, signal })
  },

  getNorm: (token: string, id: number, signal?: AbortSignal) =>
    request<Norm>(`/api/v1/norms/${id}`, { token, signal }),

  createNorm: (token: string, body: CreateNormInput) =>
    request<Norm>('/api/v1/norms', { method: 'POST', token, body }),

  setNormActivation: (token: string, id: number, isActive: boolean) =>
    request<Norm>(`/api/v1/norms/${id}/activation`, { method: 'PUT', token, body: { isActive } }),

  listCampaignNorms: (token: string, plotSeasonId: number, signal?: AbortSignal) =>
    request<PlotSeasonNorm[]>(`/api/v1/cropping-plan/${plotSeasonId}/norms`, { token, signal }),

  applyNorm: (token: string, plotSeasonId: number, body: ApplyNormInput) =>
    request<PlotSeasonNorm>(`/api/v1/cropping-plan/${plotSeasonId}/norms`, { method: 'POST', token, body }),

  removeCampaignNorm: (token: string, plotSeasonId: number, id: number) =>
    request<void>(`/api/v1/cropping-plan/${plotSeasonId}/norms/${id}`, { method: 'DELETE', token }),

  // Rendements §11.5
  getYieldMetrics: (token: string, plotSeasonId: number, signal?: AbortSignal) =>
    request<YieldMetrics>(`/api/v1/plot-seasons/${plotSeasonId}/yields`, { token, signal }),

  listYields: (token: string, params: { seasonId?: number; farmId?: number }, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    if (params.seasonId) q.set('seasonId', String(params.seasonId))
    if (params.farmId) q.set('farmId', String(params.farmId))
    return request<YieldMetrics[]>(`/api/v1/yields?${q.toString()}`, { token, signal })
  },

  // Tableau de bord / analytics
  getUnitCosts: (token: string, costObjectId: number, signal?: AbortSignal) =>
    request<UnitCosts>(`/api/v1/analytics/cost-objects/${costObjectId}/unit-costs`, { token, signal }),

  getCostAggregate: (token: string, params: { level: string; seasonId?: number; farmId?: number }, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    q.set('level', params.level)
    if (params.seasonId) q.set('seasonId', String(params.seasonId))
    if (params.farmId) q.set('farmId', String(params.farmId))
    return request<CostAggregateDto>(`/api/v1/analytics/cost-aggregate?${q.toString()}`, { token, signal })
  },

  getAggregateLevels: (token: string, signal?: AbortSignal) =>
    request<string[]>('/api/v1/analytics/levels', { token, signal }),

  // Achats - commandes et réceptions
  listPurchaseOrders: (token: string, params: { term?: string; partnerId?: number; status?: string; page?: number; size?: number }, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    if (params.term) q.set('term', params.term)
    if (params.partnerId) q.set('partnerId', String(params.partnerId))
    if (params.status) q.set('status', params.status)
    if (params.page) q.set('page', String(params.page))
    if (params.size) q.set('size', String(params.size))
    return request<PagedResult<PurchaseOrder>>(`/api/v1/purchase-orders?${q.toString()}`, { token, signal })
  },

  getPurchaseOrder: (token: string, id: number, signal?: AbortSignal) =>
    request<PurchaseOrder>(`/api/v1/purchase-orders/${id}`, { token, signal }),

  createPurchaseOrder: (token: string, body: PurchaseOrderInput) =>
    request<PurchaseOrder>('/api/v1/purchase-orders', { method: 'POST', token, body }),

  // Demandes d'achat (lot 6) : le besoin, avant la commande. Aucun montant.
  listPurchaseRequisitions: (token: string, status?: PurchaseRequisitionStatus | null, signal?: AbortSignal) =>
    request<PurchaseRequisitionListItem[]>(
      `/api/v1/purchase-requisitions${status ? `?status=${status}` : ''}`,
      { token, signal },
    ),

  getPurchaseRequisition: (token: string, id: number, signal?: AbortSignal) =>
    request<PurchaseRequisition>(`/api/v1/purchase-requisitions/${id}`, { token, signal }),

  createPurchaseRequisition: (token: string, body: PurchaseRequisitionInput) =>
    request<PurchaseRequisition>('/api/v1/purchase-requisitions', { method: 'POST', token, body }),

  approvePurchaseRequisition: (token: string, id: number) =>
    request<PurchaseRequisition>(`/api/v1/purchase-requisitions/${id}/approve`, { method: 'POST', token }),

  /** Le motif est obligatoire : un refus muet renvoie le demandeur à sa question. */
  rejectPurchaseRequisition: (token: string, id: number, reason: string) =>
    request<PurchaseRequisition>(`/api/v1/purchase-requisitions/${id}/reject`, { method: 'POST', token, body: { reason } }),

  cancelPurchaseRequisition: (token: string, id: number) =>
    request<PurchaseRequisition>(`/api/v1/purchase-requisitions/${id}/cancel`, { method: 'POST', token }),

  approvePurchaseOrder: (token: string, id: number) =>
    request<PurchaseOrder>(`/api/v1/purchase-orders/${id}/approve`, { method: 'POST', token }),

  cancelPurchaseOrder: (token: string, id: number, reason: string) =>
    request<PurchaseOrder>(`/api/v1/purchase-orders/${id}/cancel`, { method: 'POST', token, body: { reason } }),

  receivePurchaseOrder: (token: string, id: number, body: ReceiveGoodsInput) =>
    request<PurchaseReceipt>(`/api/v1/purchase-orders/${id}/receipts`, { method: 'POST', token, body }),

  updatePurchaseOrder: (token: string, id: number, body: Partial<{ farmId: number | null; warehouseId: number | null; paymentTermId: number | null; expectedDate: string | null; notes: string | null }>) =>
    request<PurchaseOrder>(`/api/v1/purchase-orders/${id}`, { method: 'PUT', token, body }),

  // Export §16.5
  downloadExport: async (token: string): Promise<Blob> => {
    const headers: Record<string, string> = {}
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await fetch(`${BASE_URL}/api/v1/exports`, { headers })
    if (!response.ok) {
      const text = await response.text()
      let code: string | undefined
      try { code = (JSON.parse(text) as { code?: string }).code } catch { /* ignore */ }
      throw new ApiError(response.status, code, text || response.statusText)
    }
    return await response.blob()
  },

  // Articles / Produits V20
  listProducts: (token: string, params: { term?: string }, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    if (params.term) q.set('term', params.term)
    return request<ProductDto[]>(`/api/v1/products?${q.toString()}`, { token, signal })
  },
  getProduct: (token: string, id: number, signal?: AbortSignal) =>
    request<ProductDto>(`/api/v1/products/${id}`, { token, signal }),
  createProduct: (token: string, body: CreateProductInput) =>
    request<ProductDto>('/api/v1/products', { method: 'POST', token, body }),
  listProductPrices: (token: string, productId: number, signal?: AbortSignal) =>
    request<ProductPriceDto[]>(`/api/v1/products/${productId}/prices`, { token, signal }),
  createProductPrice: (token: string, productId: number, body: CreateProductPriceInput) =>
    request<{ id: number }>(`/api/v1/products/${productId}/prices`, { method: 'POST', token, body }),

  // Récolte — saisie lot / liste
  createHarvestBatch: (token: string, body: CreateHarvestBatchInput) =>
    request<HarvestBatchDto>('/api/v1/harvest-batches', { method: 'POST', token, body }),
  /** Met en stock une récolte déjà pesée : un lot né en contrôle, valorisé au coût standard de l'article. */
  stockHarvestBatch: (token: string, id: number, warehouseId: number) =>
    request<HarvestBatchDto>(`/api/v1/harvest-batches/${id}/stock-entry`, { method: 'POST', token, body: { warehouseId } }),
  listHarvestBatches: (token: string, params: { plotSeasonId?: number; seasonId?: number }, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    if (params.plotSeasonId) q.set('plotSeasonId', String(params.plotSeasonId))
    if (params.seasonId) q.set('seasonId', String(params.seasonId))
    return request<HarvestBatchDto[]>(`/api/v1/harvest-batches?${q.toString()}`, { token, signal })
  },

  // Achats factures / avoirs / règlements / encours (3 voies)
  listSupplierInvoices: (token: string, params: { partnerId?: number } = {}, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    if (params.partnerId) q.set('partnerId', String(params.partnerId))
    return request<SupplierInvoiceDocument[]>(`/api/v1/supplier-invoices?${q.toString()}`, { token, signal })
  },
  getSupplierInvoice: (token: string, id: number, signal?: AbortSignal) =>
    request<SupplierInvoiceDocument>(`/api/v1/supplier-invoices/${id}`, { token, signal }),
  createSupplierInvoice: (token: string, body: CreateSupplierInvoiceInput) =>
    request<SupplierInvoiceDocument>(`/api/v1/supplier-invoices`, { method: 'POST', token, body }),
  /** Les commandes d'un fournisseur qui ont encore quelque chose à facturer : reçu, accepté, et qu'aucune facture ne tient. */
  listInvoicableOrders: (token: string, partnerId: number, signal?: AbortSignal) =>
    request<InvoicableOrder[]>(`/api/v1/supplier-invoices/invoicable-orders?partnerId=${partnerId}`, { token, signal }),
  getInvoiceMatching: (token: string, invoiceId: number, signal?: AbortSignal) =>
    request<ThreeWayMatch>(`/api/v1/supplier-invoices/${invoiceId}/matching`, { token, signal }),
  /** Approuve une facture : son rapprochement concorde, ou l'écart est assumé par écrit. Elle devient une dette. */
  approveSupplierInvoice: (token: string, id: number, body: { dueDate: string | null; varianceReason: string | null }) =>
    request<SupplierInvoiceDecision>(`/api/v1/supplier-invoices/${id}/approve`, { method: 'POST', token, body }),

  /** Refuse une facture, avec son motif : elle part en litige, et ne se règle pas. */
  rejectSupplierInvoice: (token: string, id: number, reason: string) =>
    request<SupplierInvoiceDecision>(`/api/v1/supplier-invoices/${id}/reject`, { method: 'POST', token, body: { reason } }),

  /** Fixe, déplace ou — avec `null` — retire l'échéance d'une facture fournisseur. */
  setSupplierInvoiceDueDate: (token: string, invoiceId: number, dueDate: string | null) =>
    request<{ invoiceId: number; dueDate: string | null }>(`/api/v1/supplier-invoices/${invoiceId}/due-date`, {
      method: 'PUT',
      token,
      body: { dueDate },
    }),

  /** Ce qu'on doit aux fournisseurs, rangé par ancienneté d'échéance au jour dit (aujourd'hui par défaut). */
  getSupplierPayables: (token: string, asOf?: string | null, signal?: AbortSignal) =>
    request<Receivables>(`/api/v1/purchases/payables${asOf ? `?asOf=${asOf}` : ''}`, { token, signal }),

  /** Les factures approuvées non soldées d'un fournisseur, et ses avances à imputer. */
  getPartnerPayable: (token: string, partnerId: number, signal?: AbortSignal) =>
    request<PartnerPayableDetail>(`/api/v1/purchases/payables/${partnerId}`, { token, signal }),

  listSupplierPayments: (token: string, partnerId?: number | null, signal?: AbortSignal) =>
    request<SupplierPayment[]>(`/api/v1/purchases/payments${partnerId ? `?partnerId=${partnerId}` : ''}`, { token, signal }),

  listSupplierPaymentMethods: (token: string, signal?: AbortSignal) =>
    request<SupplierPaymentMethod[]>('/api/v1/purchases/payments/methods', { token, signal }),

  recordSupplierPayment: (token: string, body: SupplierPaymentInput) =>
    request<SupplierPayment>('/api/v1/purchases/payments', { method: 'POST', token, body }),

  /** Impute tout ou partie de l'avance d'un règlement. */
  allocateSupplierPayment: (token: string, id: number, allocations: CustomerPaymentAllocation[]) =>
    request<SupplierPayment>(`/api/v1/purchases/payments/${id}/allocations`, { method: 'POST', token, body: { allocations } }),

  /** Note que la banque a débité un chèque ou une traite : il sort de l'échéancier. `null` : aujourd'hui. */
  clearSupplierPayment: (token: string, id: number, clearedOn: string | null) =>
    request<SupplierPayment>(`/api/v1/purchases/payments/${id}/clear`, { method: 'POST', token, body: { clearedOn } }),

  /** Ce qui reste à recevoir et à payer, à sa date, rangé au jour dit (aujourd'hui par défaut). */
  getPaymentSchedule: (token: string, asOf?: string | null, signal?: AbortSignal) =>
    request<PaymentSchedule>(`/api/v1/treasury/schedule${asOf ? `?asOf=${asOf}` : ''}`, { token, signal }),

  /** Les comptes de banque et de caisse, fermés compris, avec leur solde ; le pied vient du serveur. */
  getTreasurySituation: (token: string, signal?: AbortSignal) =>
    request<TreasurySituation>('/api/v1/treasury/accounts', { token, signal }),

  /** Les comptes ouverts, SANS montant : ce qu'un formulaire de paiement propose. */
  listTreasuryAccountChoices: (token: string, signal?: AbortSignal) =>
    request<TreasuryAccountChoice[]>('/api/v1/treasury/accounts/choices', { token, signal }),

  /** Le relevé d'un compte ; sans bornes, depuis son ouverture jusqu'au dernier mouvement. */
  getTreasuryStatement: (token: string, accountId: number, from: string | null, to: string | null, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    if (from !== null) q.set('from', from)
    if (to !== null) q.set('to', to)
    const suffixe = q.toString()
    return request<AccountStatement>(`/api/v1/treasury/accounts/${accountId}/statement${suffixe === '' ? '' : `?${suffixe}`}`, { token, signal })
  },

  createTreasuryAccount: (token: string, body: TreasuryAccountInput) =>
    request<TreasuryAccount>('/api/v1/treasury/accounts', { method: 'POST', token, body }),

  /** Corrige le nom et la référence bancaire. Le code, la nature et l'ouverture ne se réécrivent pas. */
  renameTreasuryAccount: (token: string, id: number, body: { name: string; bankReference: string | null }) =>
    request<TreasuryAccount>(`/api/v1/treasury/accounts/${id}`, { method: 'PUT', token, body }),

  closeTreasuryAccount: (token: string, id: number) =>
    request<TreasuryAccount>(`/api/v1/treasury/accounts/${id}/close`, { method: 'POST', token }),

  reopenTreasuryAccount: (token: string, id: number) =>
    request<TreasuryAccount>(`/api/v1/treasury/accounts/${id}/reopen`, { method: 'POST', token }),

  /** Seul un compte qu'aucun paiement ne désigne se supprime. */
  deleteTreasuryAccount: (token: string, id: number) =>
    request<void>(`/api/v1/treasury/accounts/${id}`, { method: 'DELETE', token }),

  /** Les deux cents mouvements saisis les plus récents, annulés compris ; d'un compte — origine ou destination — ou de tous. */
  listTreasuryEntries: (token: string, accountId?: number | null, signal?: AbortSignal) =>
    request<TreasuryEntry[]>(`/api/v1/treasury/entries${accountId ? `?accountId=${accountId}` : ''}`, { token, signal }),

  /** Les natures de mouvement acceptées : un virement demande un second compte, pas un sens. */
  listTreasuryEntryCategories: (token: string, signal?: AbortSignal) =>
    request<TreasuryEntryCategory[]>('/api/v1/treasury/entries/categories', { token, signal }),

  /** Saisit un mouvement — ou un virement, qui sort d'un compte et entre dans un autre en une seule écriture. */
  recordTreasuryEntry: (token: string, body: TreasuryEntryInput) =>
    request<TreasuryEntry>('/api/v1/treasury/entries', { method: 'POST', token, body }),

  /** Le motif est obligatoire. Un virement annulé revient dans ses deux comptes. */
  cancelTreasuryEntry: (token: string, id: number, reason: string) =>
    request<TreasuryEntry>(`/api/v1/treasury/entries/${id}/cancel`, { method: 'POST', token, body: { reason } }),

  /** Les autres engagements : tous ceux qui doivent encore, à leur date, avec leur pied ; et les derniers soldés ou annulés. */
  getCommitments: (token: string, signal?: AbortSignal) =>
    request<Commitments>('/api/v1/treasury/commitments', { token, signal }),

  /** Les natures d'engagement, les pas d'une série et son nombre maximal d'échéances. */
  getCommitmentOptions: (token: string, signal?: AbortSignal) =>
    request<CommitmentOptions>('/api/v1/treasury/commitments/options', { token, signal }),

  /** Saisit un engagement, ou une série : autant de lignes, chacune avec son échéance. */
  createCommitments: (token: string, body: CommitmentInput) =>
    request<{ count: number; firstDueDate: string; lastDueDate: string }>('/api/v1/treasury/commitments', { method: 'POST', token, body }),

  /** Paie tout ou partie d'un engagement, depuis une banque ou une caisse. Sans montant : tout ce qui reste. */
  payCommitment: (token: string, id: number, body: CommitmentPaymentInput) =>
    request<Commitment>(`/api/v1/treasury/commitments/${id}/pay`, { method: 'POST', token, body }),

  /** Le motif est obligatoire. Annulé, un engagement ne doit plus rien ; ce qui en a été payé reste payé. */
  cancelCommitment: (token: string, id: number, reason: string) =>
    request<Commitment>(`/api/v1/treasury/commitments/${id}/cancel`, { method: 'POST', token, body: { reason } }),

  /** Les indicateurs du dirigeant pour une campagne ; sans campagne demandée, celle en cours. */
  getExecutiveDashboard: (token: string, seasonId: number | null, signal?: AbortSignal) =>
    request<ExecutiveDashboard>(`/api/v1/analytics/executive${seasonId === null ? '' : `?seasonId=${seasonId}`}`, { token, signal }),

  /** Le centre d'alertes : toutes les alertes que l'utilisateur a le droit de lire, famille par famille. */
  getAlertCenter: (token: string, signal?: AbortSignal) => request<AlertCenter>('/api/v1/alerts', { token, signal }),

  /** Les relevés bancaires d'un compte, du plus récent au plus ancien, avec leur écart. */
  listBankStatements: (token: string, accountId: number, signal?: AbortSignal) =>
    request<BankStatement[]>(`/api/v1/treasury/bank-statements?accountId=${accountId}`, { token, signal }),

  /** Un relevé, ce qu'on y a pointé, et ce qui reste à pointer sur son compte. */
  getBankReconciliation: (token: string, statementId: number, signal?: AbortSignal) =>
    request<BankReconciliation>(`/api/v1/treasury/bank-statements/${statementId}`, { token, signal }),

  createBankStatement: (token: string, body: BankStatementInput & { accountId: number }) =>
    request<BankStatement>('/api/v1/treasury/bank-statements', { method: 'POST', token, body }),

  /** Corrige la date, la référence et les soldes d'un relevé, pointé ou non. Son compte ne se réécrit pas. */
  correctBankStatement: (token: string, id: number, body: BankStatementInput) =>
    request<BankStatement>(`/api/v1/treasury/bank-statements/${id}`, { method: 'PUT', token, body }),

  /** Supprime un relevé sur lequel rien n'est pointé. */
  deleteBankStatement: (token: string, id: number) =>
    request<void>(`/api/v1/treasury/bank-statements/${id}`, { method: 'DELETE', token }),

  /** Pointe un mouvement du compte sur ce relevé. */
  pointBankMovement: (token: string, statementId: number, sourceKind: TreasuryMovement['sourceKind'], sourceId: number) =>
    request<BankStatement>(`/api/v1/treasury/bank-statements/${statementId}/marks`, { method: 'POST', token, body: { sourceKind, sourceId } }),

  /** Retire un pointage : le mouvement est de nouveau à pointer. */
  unpointBankMovement: (token: string, statementId: number, sourceKind: TreasuryMovement['sourceKind'], sourceId: number) =>
    request<BankStatement>(`/api/v1/treasury/bank-statements/${statementId}/marks/${sourceKind}/${sourceId}`, { method: 'DELETE', token }),

  /** Les paiements qui font bouger de l'argent et ne désignent aucun compte. */
  listUnassignedPayments: (token: string, signal?: AbortSignal) =>
    request<UnassignedPayment[]>('/api/v1/treasury/unassigned-payments', { token, signal }),

  assignPaymentAccount: (token: string, sourceKind: UnassignedPayment['sourceKind'], sourceId: number, accountId: number) =>
    request<void>(`/api/v1/treasury/unassigned-payments/${sourceKind}/${sourceId}/assign`, { method: 'POST', token, body: { accountId } }),

  /** Le motif est obligatoire : il distingue le chèque revenu de l'erreur de saisie. */
  cancelSupplierPayment: (token: string, id: number, reason: string) =>
    request<SupplierPayment>(`/api/v1/purchases/payments/${id}/cancel`, { method: 'POST', token, body: { reason } }),

  // Frais accessoires V18
  createLandedCost: (token: string, body: CreateLandedCostInput) =>
    request<LandedCostDto>('/api/v1/landed-costs', { method: 'POST', token, body }),
  allocateLandedCost: (token: string, id: number) =>
    request<LandedCostAllocationDto[]>(`/api/v1/landed-costs/${id}/allocate`, { method: 'POST', token }),

  // Utilisateurs
  listUsers: (token: string, signal?: AbortSignal) =>
    request<UserDto[]>('/api/v1/users', { token, signal }),
  createUser: (token: string, body: CreateUserInput) =>
    request<UserDto>('/api/v1/users', { method: 'POST', token, body }),
  getUser: (token: string, id: number, signal?: AbortSignal) =>
    request<UserDetail>(`/api/v1/users/${id}`, { token, signal }),
  updateUser: (token: string, id: number, body: UpdateUserInput) =>
    request<UserDetail>(`/api/v1/users/${id}`, { method: 'PUT', token, body }),
  setUserScopes: (token: string, id: number, body: SetUserScopesInput) =>
    request<UserDetail>(`/api/v1/users/${id}/scopes`, { method: 'PUT', token, body }),

  /** Rôle et capacités de l'utilisateur connecté, lus de la matrice serveur (§4.4). */
  getMyProfile: (token: string, signal?: AbortSignal) =>
    request<MyProfile>('/api/v1/me', { token, signal }),

  // Assolement en masse (§7.3)
  listUnassignedPlots: (
    token: string,
    params: { seasonId: number; farmId?: number },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams({ seasonId: String(params.seasonId) })
    if (params.farmId) query.set('farmId', String(params.farmId))
    return request<UnassignedPlot[]>(`/api/v1/cropping-plan/unassigned?${query}`, { token, signal })
  },
  bulkAssignCrop: (token: string, body: BulkAssignInput) =>
    request<BulkAssignResult>('/api/v1/cropping-plan/bulk', { method: 'POST', token, body }),

  // Champs personnalisés
  listCustomFieldEntities: (token: string, signal?: AbortSignal) =>
    request<string[]>('/api/v1/custom-fields/entities', { token, signal }),
  listCustomFields: (token: string, entityTable: string, signal?: AbortSignal) =>
    request<CustomFieldDefinition[]>(`/api/v1/custom-fields?entityTable=${entityTable}`, { token, signal }),
  createCustomField: (token: string, body: CreateCustomFieldInput) =>
    request<CustomFieldDefinition>('/api/v1/custom-fields', { method: 'POST', token, body }),
  getCustomFieldValues: (token: string, entityTable: string, entityId: number, signal?: AbortSignal) =>
    request<CustomFieldValue[]>(`/api/v1/custom-fields/values/${entityTable}/${entityId}`, { token, signal }),
  saveCustomFieldValues: (
    token: string,
    entityTable: string,
    entityId: number,
    values: Record<string, string | null>,
  ) =>
    request<CustomFieldValue[]>(`/api/v1/custom-fields/values/${entityTable}/${entityId}`, {
      method: 'PUT',
      token,
      body: values,
    }),

  // Subventions V7
  listSubsidies: (token: string, signal?: AbortSignal) =>
    request<SubsidyDto[]>('/api/v1/subsidies', { token, signal }),
  createSubsidy: (token: string, body: CreateSubsidyInput) =>
    request<SubsidyDto>('/api/v1/subsidies', { method: 'POST', token, body }),
  allocateSubsidy: (token: string, id: number, body: AllocateSubsidyInput) =>
    request<SubsidyAllocationResult>(`/api/v1/subsidies/${id}/allocate`, { method: 'POST', token, body }),
  getSubsidyAllocations: (token: string, id: number, signal?: AbortSignal) =>
    request<SubsidyAllocation[]>(`/api/v1/subsidies/${id}/allocations`, { token, signal }),

  // Alertes §10.3 et cahier de plaine
  checkOperationWarnings: (token: string, body: CheckWarningsInput) =>
    request<WarningsResponse>('/api/v1/operations/warnings/check', { method: 'POST', token, body }),
  getFieldBook: (token: string, plotSeasonId: number, signal?: AbortSignal) =>
    request<FieldBookEntry[]>(`/api/v1/operations/field-book/${plotSeasonId}`, { token, signal }),

  // Valorisation V3
  getHarvestValuation: (token: string, plotSeasonId: number, signal?: AbortSignal) =>
    request<HarvestValuation>(`/api/v1/harvest-valuation/${plotSeasonId}`, { token, signal }),

  // Avoirs fournisseur
  listCreditNoteReasons: (token: string, signal?: AbortSignal) =>
    request<string[]>('/api/v1/supplier-invoices/credit-note-reasons', { token, signal }),
  createSupplierCreditNote: (token: string, invoiceId: number, body: CreateCreditNoteInput) =>
    request<SupplierCreditNoteResult>(`/api/v1/supplier-invoices/${invoiceId}/credit-note`, {
      method: 'POST',
      token,
      body,
    }),

  // Reprise des données du client
  listTakeoverTemplates: (token: string, signal?: AbortSignal) =>
    request<Record<string, string[]>>('/api/v1/reprise/templates', { token, signal }),
  analyseTakeover: (token: string, body: AnalyseTakeoverInput) =>
    request<TakeoverBatch>('/api/v1/reprise/analyse', { method: 'POST', token, body }),
  loadTakeover: (token: string, batchId: number) =>
    request<TakeoverBatch>(`/api/v1/reprise/${batchId}/load`, { method: 'POST', token }),
  listTakeoverBatches: (token: string, signal?: AbortSignal) =>
    request<TakeoverBatch[]>('/api/v1/reprise', { token, signal }),
  getTakeoverBatch: (token: string, batchId: number, signal?: AbortSignal) =>
    request<TakeoverBatch>(`/api/v1/reprise/${batchId}`, { token, signal }),

  // Factures de VENTE et leurs avoirs
  /** Les bons de livraison d'un client qui ont encore quelque chose à facturer : livrés, et qu'aucune facture n'a repris. */
  listInvoicableDeliveries: (token: string, partnerId: number, signal?: AbortSignal) =>
    request<InvoicableDelivery[]>(`/api/v1/customer-invoices/invoicable-deliveries?partnerId=${partnerId}`, { token, signal }),

  /** Les taux de taxe en vigueur aujourd'hui, nommés dans la langue du lecteur. */
  listInvoiceTaxRates: (token: string, signal?: AbortSignal) =>
    request<InvoiceTaxRate[]>('/api/v1/customer-invoices/tax-rates', { token, signal }),

  /** Émet une facture reprenant un ou plusieurs bons livrés. La taxe, les totaux et le numéro sont posés par le serveur. */
  createCustomerInvoice: (token: string, body: CreateCustomerInvoiceInput) =>
    request<{ id: number; documentNumber: string }>('/api/v1/customer-invoices', { method: 'POST', token, body }),

  listCustomerInvoices: (token: string, partnerId?: number, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    if (partnerId !== undefined) q.set('partnerId', String(partnerId))
    return request<CustomerInvoiceSummary[]>(`/api/v1/customer-invoices?${q}`, { token, signal })
  },
  createCustomerCreditNote: (token: string, invoiceId: number, body: CustomerCreditNoteInput) =>
    request<{ id: number; documentNumber: string }>(`/api/v1/customer-invoices/${invoiceId}/credit-notes`, {
      method: 'POST',
      token,
      body,
    }),

  // Journal d audit (L1-07)
  searchAudit: (token: string, params: AuditSearchParams, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    if (params.entityTable) q.set('entityTable', params.entityTable)
    if (params.entityId !== undefined) q.set('entityId', String(params.entityId))
    if (params.userId !== undefined) q.set('userId', String(params.userId))
    if (params.action) q.set('action', params.action)
    if (params.from) q.set('from', params.from)
    if (params.to) q.set('to', params.to)
    if (params.page) q.set('page', String(params.page))
    if (params.size) q.set('size', String(params.size))
    return request<PagedResult<AuditEntry>>(`/api/v1/audit?${q}`, { token, signal })
  },

  // Cycle de vie des campagnes (§10.5)
  openSeason: (token: string, body: OpenSeasonInput) =>
    request<SeasonLifecycle>('/api/v1/seasons', { method: 'POST', token, body }),
  getSeasonBlockers: (token: string, id: number, signal?: AbortSignal) =>
    request<ClosingBlocker[]>(`/api/v1/seasons/${id}/closing-blockers`, { token, signal }),
  closeSeason: (token: string, id: number) =>
    request<SeasonLifecycle>(`/api/v1/seasons/${id}/close`, { method: 'POST', token }),
  reopenSeason: (token: string, id: number, reason: string) =>
    request<SeasonLifecycle>(`/api/v1/seasons/${id}/reopen`, {
      method: 'POST',
      token,
      body: { reason },
    }),

  // Alertes de stock (L5-09)
  listStockAlerts: (
    token: string,
    params: { warehouseId?: number; expiryWindowDays?: number } = {},
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams()
    if (params.warehouseId !== undefined) q.set('warehouseId', String(params.warehouseId))
    // `!== undefined` et non la vérité du nombre : zéro est une fenêtre VALIDE — « ce
    // qui périme aujourd'hui » — et le serveur ne refuse que le négatif. Le test de
    // vérité la remplaçait en silence par la valeur par défaut de trente jours, et
    // l'écran affichait des lots à trois semaines en réponse à « aujourd'hui ».
    if (params.expiryWindowDays !== undefined) {
      q.set('expiryWindowDays', String(params.expiryWindowDays))
    }
    return request<StockAlert[]>(`/api/v1/stock/alerts?${q}`, { token, signal })
  },

  // Journal du stock (L5-11)
  listStockMovements: (
    token: string,
    params: {
      warehouseId?: number
      productId?: number
      from?: string
      to?: string
      limit?: number
    } = {},
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams()
    if (params.warehouseId !== undefined) q.set('warehouseId', String(params.warehouseId))
    if (params.productId !== undefined) q.set('productId', String(params.productId))
    if (params.from) q.set('from', params.from)
    if (params.to) q.set('to', params.to)
    if (params.limit !== undefined) q.set('limit', String(params.limit))
    return request<StockMovementRow[]>(`/api/v1/stock/movements?${q}`, { token, signal })
  },

  listStockLots: (
    token: string,
    params: { warehouseId?: number; expiringWithinDays?: number } = {},
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams()
    if (params.warehouseId !== undefined) q.set('warehouseId', String(params.warehouseId))
    if (params.expiringWithinDays !== undefined) {
      q.set('expiringWithinDays', String(params.expiringWithinDays))
    }
    return request<StockLotRow[]>(`/api/v1/stock/lots?${q}`, { token, signal })
  },

  /** Cherche un lot à tracer, par son code ou par le n° de lot du fournisseur. Vingt au plus. */
  searchTraceableLots: (token: string, term: string, signal?: AbortSignal) =>
    request<TracedLot[]>(`/api/v1/stock/lots/search?${new URLSearchParams({ term })}`, { token, signal }),

  /** La traçabilité d'un lot, dans les deux sens. Aucun montant. */
  getLotTrace: (token: string, lotId: number, signal?: AbortSignal) =>
    request<LotTrace>(`/api/v1/stock/lots/${lotId}/trace`, { token, signal }),

  /** Détache des parts d'un lot, dans un dépôt : chacune devient un lot fils qui hérite de son statut. */
  splitStockLot: (token: string, lotId: number, body: LotSplitInput) =>
    request<LotSplit>(`/api/v1/stock/lots/${lotId}/split`, { method: 'POST', token, body }),

  listQualityControls: (
    token: string,
    params: { lotId?: number; warehouseId?: number; page?: number } = {},
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams()
    if (params.lotId !== undefined) q.set('lotId', String(params.lotId))
    if (params.warehouseId !== undefined) q.set('warehouseId', String(params.warehouseId))
    if (params.page !== undefined) q.set('page', String(params.page))
    return request<PagedResult<QualityControl>>(`/api/v1/stock/quality-controls?${q}`, { token, signal })
  },

  /** La grille à remplir : la norme en vigueur de l'article du lot, et ses paramètres. */
  getLotControlSheet: (token: string, lotId: number, signal?: AbortSignal) =>
    request<ControlSheet>(`/api/v1/stock/lots/${lotId}/control-sheet`, { token, signal }),

  /** Des MESURES, et rien d'autre : la base juge, déduit le résultat et libère ou bloque le lot. */
  recordMeasuredControl: (token: string, body: MeasuredControlInput) =>
    request<QualityControl>('/api/v1/stock/quality-controls', { method: 'POST', token, body }),

  /** Décision humaine sur un lot dont l'article n'a pas de norme. Refusée s'il en a une. */
  recordManualControl: (token: string, body: ManualControlInput) =>
    request<QualityControl>('/api/v1/stock/quality-controls/manual', { method: 'POST', token, body }),

  /** Dérogation motivée sur un lot bloqué : libération conditionnelle, ou rejet. */
  recordQualityOverride: (token: string, body: QualityOverrideInput) =>
    request<QualityControl>('/api/v1/stock/quality-controls/override', { method: 'POST', token, body }),

  // Normes de produit (§14 du document client, migration 0085)
  listProductStandards: (
    token: string,
    params: { productId?: number; status?: ProductStandard['status'] } = {},
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams()
    if (params.productId !== undefined) q.set('productId', String(params.productId))
    if (params.status) q.set('status', params.status)
    return request<ProductStandard[]>(`/api/v1/product-standards?${q}`, { token, signal })
  },

  getProductStandard: (token: string, id: number, signal?: AbortSignal) =>
    request<ProductStandard>(`/api/v1/product-standards/${id}`, { token, signal }),

  createProductStandard: (token: string, body: ProductStandardInput) =>
    request<ProductStandard>('/api/v1/product-standards', { method: 'POST', token, body }),

  updateProductStandard: (token: string, id: number, body: ProductStandardInput) =>
    request<ProductStandard>(`/api/v1/product-standards/${id}`, { method: 'PUT', token, body }),

  activateProductStandard: (token: string, id: number) =>
    request<ProductStandard>(`/api/v1/product-standards/${id}/activate`, { method: 'POST', token }),

  retireProductStandard: (token: string, id: number) =>
    request<ProductStandard>(`/api/v1/product-standards/${id}/retire`, { method: 'POST', token }),

  buildStockCountSheet: (token: string, warehouseId: number, signal?: AbortSignal) =>
    request<StockCountRow[]>(`/api/v1/stock/count-sheet?warehouseId=${warehouseId}`, {
      token,
      signal,
    }),

  // Contrats de vente et encours client (L8-08, L8-10)
  listSalesContracts: (
    token: string,
    params: { partnerId?: number; seasonId?: number } = {},
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams()
    if (params.partnerId !== undefined) q.set('partnerId', String(params.partnerId))
    if (params.seasonId !== undefined) q.set('seasonId', String(params.seasonId))
    return request<ContractProgress[]>(`/api/v1/sales/contracts?${q}`, { token, signal })
  },

  /** Crée un contrat de vente, accepté à sa création. Sans numéro, le serveur l'alloue. */
  createSalesContract: (token: string, body: SalesContractInput) =>
    request<SalesContract>('/api/v1/sales/contracts', { method: 'POST', token, body }),

  getSalesContract: (token: string, id: number, signal?: AbortSignal) =>
    request<SalesContract>(`/api/v1/sales/contracts/${id}`, { token, signal }),

  /**
   * Émet un bon de livraison, posté : chaque ligne d'un article géré en stock SORT du
   * dépôt, et pour un article suivi par lot c'est le lot désigné qui sort.
   */
  createDeliveryFromContract: (token: string, contractId: number, body: DeliveryInput) =>
    request<DeliveryNote>(`/api/v1/sales/contracts/${contractId}/deliveries`, { method: 'POST', token, body }),

  getCustomerOutstanding: (token: string, partnerId: number, signal?: AbortSignal) =>
    request<CustomerOutstanding>(`/api/v1/sales/customers/${partnerId}/outstanding`, {
      token,
      signal,
    }),

  // Parcelles les plus proches (L10-10)
  listPlotsNear: (token: string, latitude: number, longitude: number, limit = 10) =>
    request<NearbyPlot[]>(
      `/api/v1/plots/near?latitude=${latitude}&longitude=${longitude}&limit=${limit}`,
      { token },
    ),

  // Interventions — liste paginée (dernières interventions du tableau de bord)
  listOperations: (token: string, params: { page?: number; size?: number } = {}, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    if (params.page) q.set('page', String(params.page))
    if (params.size) q.set('size', String(params.size))
    return request<PagedResult<OperationSummary>>(`/api/v1/operations?${q}`, { token, signal })
  },

  // Rapports §15.1
  listReports: (token: string, signal?: AbortSignal) =>
    request<string[]>('/api/v1/reports', { token, signal }),

  // Panneau plateforme (L6-02). Les trois routes portent RequirePlatformAdmin : un jeton
  // d'administrateur client reçoit un 403 « platform.forbidden », et non une liste vide —
  // une liste vide se lirait « aucun client » au lieu de « pas le droit de les voir ».
  listPlatformClients: (token: string, signal?: AbortSignal) =>
    request<PlatformClient[]>('/api/v1/platform/clients', { token, signal }),

  // 201 avec l'identifiant de l'entreprise née. Les refus portent leur code stable —
  // platform.legal_name_required, platform.modules_required, platform.module_unknown,
  // platform.plan_unknown, platform.email_taken — que l'écran traduit.
  createPlatformClient: (token: string, client: NewPlatformClient) =>
    request<{ organizationId: number }>('/api/v1/platform/clients', {
      method: 'POST',
      body: client,
      token,
    }),

  // POST et non PATCH : ce n'est pas un champ qu'on modifie, c'est une porte qu'on ferme.
  // Désactiver coupe la session de tout le monde chez ce client — core.find_user_for_sign_in
  // filtre sur is_active — et l'écran le dit avant d'appeler.
  setPlatformClientActive: (token: string, organizationId: number, isActive: boolean) =>
    request<{ organizationId: number; isActive: boolean }>(
      `/api/v1/platform/clients/${organizationId}/activation`,
      { method: 'POST', body: { isActive }, token },
    ),

  // Fiche client (L6-03). Un seul aller-retour pour les quatre sections ET le catalogue des
  // formules : sans les plafonds de la formule visée, l'avertissement de déclassement
  // n'aurait aucun chiffre à donner.
  getPlatformClient: (token: string, organizationId: number, signal?: AbortSignal) =>
    request<PlatformClientDetail>(`/api/v1/platform/clients/${organizationId}`, { token, signal }),

  // SUSPENDRE N'EST PAS DÉSACTIVER. Ceci laisse le client se connecter et tout lire ; il ne
  // peut simplement plus rien créer. C'est `setPlatformClientActive` qui ferme la porte.
  // Le motif est OBLIGATOIRE pour « suspended » et « cancelled » : la base l'exige
  // (ck_audit_module_change_reason, ck_subscription_cancellation), et l'écran le demande
  // avant plutôt que d'échouer après.
  setPlatformSubscriptionStatus: (
    token: string,
    organizationId: number,
    status: 'active' | 'suspended' | 'cancelled',
    reason: string | null,
  ) =>
    request<{ organizationId: number; status: string }>(
      `/api/v1/platform/clients/${organizationId}/subscription/status`,
      { method: 'POST', body: { status, reason }, token },
    ),

  // CRÉE un abonnement, là où les trois appels voisins en modifient un. Refusé avec
  // `platform.subscription_exists` dès qu'une ligne existe, quel que soit son statut : un
  // abonnement résilié se réactive, il ne se double pas.
  grantPlatformSubscription: (token: string, organizationId: number, planCode: string, reason: string | null) =>
    request<{ organizationId: number; planCode: string }>(
      `/api/v1/platform/clients/${organizationId}/subscription`,
      { method: 'POST', body: { planCode, reason }, token },
    ),

  // Un déclassement sous l'usage PASSE : le client garde et lit ses données. L'écran a
  // affiché les chiffres avant de faire confirmer.
  changePlatformClientPlan: (token: string, organizationId: number, planCode: string, reason: string | null) =>
    request<{ organizationId: number; planCode: string }>(
      `/api/v1/platform/clients/${organizationId}/subscription/plan`,
      { method: 'POST', body: { planCode, reason }, token },
    ),

  // Depuis l'échéance actuelle et non depuis aujourd'hui : sinon prolonger un abonnement en
  // cours le raccourcirait.
  extendPlatformSubscription: (token: string, organizationId: number, months: number) =>
    request<{ organizationId: number; currentPeriodEnd: string }>(
      `/api/v1/platform/clients/${organizationId}/subscription/extension`,
      { method: 'POST', body: { months }, token },
    ),

  // Fermer exige un motif, et fermer la dernière activité ouverte est refusé
  // (`platform.last_module`) : un client sans activité ne pourrait plus rien faire, et rien
  // ne le lui dirait.
  setPlatformClientModule: (
    token: string,
    organizationId: number,
    moduleCode: string,
    enabled: boolean,
    reason: string | null,
  ) =>
    request<{ organizationId: number; moduleCode: string; isEnabled: boolean; changed: boolean }>(
      `/api/v1/platform/clients/${organizationId}/modules/${moduleCode}`,
      { method: 'POST', body: { enabled, reason }, token },
    ),

  // Catalogue commercial (lot 1 SaaS, migration 0076). Toutes ces routes portent
  // RequirePlatformAdmin ; les refus reviennent avec leur code stable (platform.plan_*,
  // platform.feature_unknown, platform.reason_required…) que l'écran traduit.
  listPlatformPlans: (token: string, signal?: AbortSignal) =>
    request<PlanAdmin[]>('/api/v1/platform/plans', { token, signal }),

  listPlatformFeatures: (token: string, signal?: AbortSignal) =>
    request<FeatureInfo[]>('/api/v1/platform/features', { token, signal }),

  createPlatformPlan: (token: string, body: PlanPayload) =>
    request<{ id: number }>('/api/v1/platform/plans', { method: 'POST', token, body }),

  // 409 platform.plan_stale quand un autre a enregistré la formule depuis sa lecture.
  updatePlatformPlan: (token: string, id: number, body: PlanPayload) =>
    request<{ id: number }>(`/api/v1/platform/plans/${id}`, { method: 'PUT', token, body }),

  setPlatformPlanActive: (token: string, id: number, isActive: boolean) =>
    request<{ id: number; isActive: boolean; changed: boolean }>(`/api/v1/platform/plans/${id}/activation`, {
      method: 'POST',
      token,
      body: { isActive },
    }),

  listPlatformClientFeatures: (token: string, organizationId: number, signal?: AbortSignal) =>
    request<ClientFeature[]>(`/api/v1/platform/clients/${organizationId}/features`, { token, signal }),

  // included nul RETIRE l'exception : la formule reprend la main.
  setPlatformClientFeature: (
    token: string,
    organizationId: number,
    code: string,
    body: { included: boolean | null; reason: string | null; expiresOn: string | null },
  ) =>
    request<{ code: string; changed: boolean }>(`/api/v1/platform/clients/${organizationId}/features/${code}`, {
      method: 'PUT',
      token,
      body,
    }),

  // Ce dont le menu du client se construit. Le menu n'est qu'un reflet : les routes sont
  // gardées par RequireFeature, avec la même règle.
  getEntitlements: (token: string, signal?: AbortSignal) =>
    request<{ features: string[] }>('/api/v1/entitlements', { token, signal }),

  // Cycle de vie des abonnements (lot 2 SaaS, migration 0077).
  getPlatformClientLifecycle: (token: string, organizationId: number, signal?: AbortSignal) =>
    request<{ lifecycle: ClientLifecycle | null; events: SubscriptionEvent[] }>(
      `/api/v1/platform/clients/${organizationId}/lifecycle`,
      { token, signal },
    ),

  // La date d'effet est choisie par la base : le lendemain de l'échéance.
  schedulePlatformPlanChange: (token: string, organizationId: number, planCode: string, reason: string | null) =>
    request<{ planCode: string; effectiveOn: string }>(
      `/api/v1/platform/clients/${organizationId}/subscription/scheduled-plan`,
      { method: 'POST', token, body: { planCode, reason } },
    ),

  cancelPlatformPlanChange: (token: string, organizationId: number) =>
    request<{ changed: boolean }>(`/api/v1/platform/clients/${organizationId}/subscription/scheduled-plan`, {
      method: 'DELETE',
      token,
    }),

  setPlatformCancelAtPeriodEnd: (token: string, organizationId: number, cancel: boolean, reason: string | null) =>
    request<{ changed: boolean }>(`/api/v1/platform/clients/${organizationId}/subscription/cancel-at-period-end`, {
      method: 'POST',
      token,
      body: { cancel, reason },
    }),

  getBillingPolicy: (token: string, signal?: AbortSignal) =>
    request<BillingPolicy>('/api/v1/platform/billing-policy', { token, signal }),

  setBillingPolicy: (token: string, policy: BillingPolicy) =>
    request<BillingPolicy>('/api/v1/platform/billing-policy', { method: 'PUT', token, body: policy }),

  runPlatformLifecycle: (token: string) =>
    request<{ day: string; counts: Record<string, number>; total: number }>('/api/v1/platform/lifecycle/run', {
      method: 'POST',
      token,
    }),

  listSubscriptionNotices: (token: string, signal?: AbortSignal) =>
    request<SubscriptionNotice[]>('/api/v1/subscription/notices', { token, signal }),

  markSubscriptionNoticeRead: (token: string, id: number) =>
    request<void>(`/api/v1/subscription/notices/${id}/read`, { method: 'POST', token }),

  // Actifs agricoles, lot A : socle et fiche. Les montants restent bruts : c'est
  // l'écran qui formate via <Nombre>, jamais le client HTTP.
  listAssets: (
    token: string,
    params: { term?: string; categoryId?: number; status?: string; page?: number; includeInactive?: boolean },
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams()
    if (params.term) q.set('term', params.term)
    if (params.categoryId) q.set('categoryId', String(params.categoryId))
    if (params.status) q.set('status', params.status)
    if (params.page) q.set('page', String(params.page))
    if (params.includeInactive) q.set('includeInactive', 'true')
    return request<PagedResult<FixedAsset>>(`/api/v1/assets?${q.toString()}`, { token, signal })
  },

  getAsset: (token: string, id: number, signal?: AbortSignal) =>
    request<FixedAsset>(`/api/v1/assets/${id}`, { token, signal }),

  createAsset: (token: string, body: CreateFixedAssetInput) =>
    request<FixedAsset>('/api/v1/assets', { method: 'POST', token, body }),

  transferAsset: (token: string, id: number, body: TransferFixedAssetInput) =>
    request<AssetTransfer>(`/api/v1/assets/${id}/transfers`, { method: 'POST', token, body }),

  disposeAsset: (token: string, id: number, body: DisposeFixedAssetInput) =>
    request<void>(`/api/v1/assets/${id}/disposal`, { method: 'POST', token, body }),

  // Rend `null` quand l'actif n'est pas cédé : l'absence de cession est l'état
  // normal du parc, pas une erreur. Seul un actif introuvable donne un 404.
  getAssetDisposal: (token: string, id: number, signal?: AbortSignal) =>
    request<AssetDisposal | null>(`/api/v1/assets/${id}/disposal`, { token, signal }),

  listInProgressExpenses: (token: string, id: number, signal?: AbortSignal) =>
    request<InProgressExpenses>(`/api/v1/assets/${id}/in-progress-expenses`, { token, signal }),

  addInProgressExpense: (token: string, id: number, body: AddInProgressExpenseInput) =>
    request<InProgressExpense>(`/api/v1/assets/${id}/in-progress-expenses`, { method: 'POST', token, body }),

  listAssetCategories: (token: string, params: { term?: string; page?: number } = {}, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    if (params.term) q.set('term', params.term)
    if (params.page) q.set('page', String(params.page))
    return request<PagedResult<AssetCategory>>(`/api/v1/asset-categories?${q.toString()}`, { token, signal })
  },

  createAssetCategory: (token: string, body: CreateAssetCategoryInput) =>
    request<AssetCategory>('/api/v1/asset-categories', { method: 'POST', token, body }),

  // Actifs, lots B à F : compteurs, amortissement, entretien, coût horaire,
  // inventaire et tableau de bord.
  updateAsset: (token: string, id: number, body: UpdateFixedAssetInput) =>
    request<FixedAsset>(`/api/v1/assets/${id}`, { method: 'PUT', token, body }),

  listMeters: (token: string, id: number, meterKind?: string, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    if (meterKind) q.set('meterKind', meterKind)
    return request<MeterReading[]>(`/api/v1/assets/${id}/meters?${q.toString()}`, { token, signal })
  },

  recordMeter: (token: string, id: number, body: RecordMeterInput) =>
    request<MeterReading>(`/api/v1/assets/${id}/meters`, { method: 'POST', token, body }),

  listAssetUsages: (token: string, id: number, params: { page?: number } = {}, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    if (params.page) q.set('page', String(params.page))
    return request<PagedResult<AssetUsage>>(`/api/v1/assets/${id}/usages?${q.toString()}`, { token, signal })
  },

  attachAssetUsage: (token: string, body: AttachAssetUsageInput) =>
    request<void>('/api/v1/assets/usages/attach', { method: 'POST', token, body }),

  getDepreciation: (token: string, id: number, signal?: AbortSignal) =>
    request<DepreciationTable>(`/api/v1/assets/${id}/depreciation`, { token, signal }),

  createDepreciationPlan: (token: string, id: number, body: CreateDepreciationPlanInput) =>
    request<DepreciationTable>(`/api/v1/assets/${id}/depreciation/plan`, { method: 'POST', token, body }),

  recomputeDepreciation: (token: string, id: number) =>
    request<DepreciationTable>(`/api/v1/assets/${id}/depreciation/recompute`, { method: 'POST', token }),

  addComponent: (token: string, id: number, body: AddComponentInput) =>
    request<void>(`/api/v1/assets/${id}/components`, { method: 'POST', token, body }),

  revalueAsset: (token: string, id: number, body: RevalueAssetInput) =>
    request<void>(`/api/v1/assets/${id}/revaluations`, { method: 'POST', token, body }),

  listComponents: (token: string, id: number, signal?: AbortSignal) =>
    request<ComponentRow[]>(`/api/v1/assets/${id}/components`, { token, signal }),

  getLease: (token: string, id: number, signal?: AbortSignal) =>
    request<LeaseRow | null>(`/api/v1/assets/${id}/lease`, { token, signal }),

  listMaintenances: (token: string, id: number, fiscalYear?: number, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    if (fiscalYear) q.set('fiscalYear', String(fiscalYear))
    return request<MaintenanceRow[]>(`/api/v1/assets/${id}/maintenances?${q.toString()}`, { token, signal })
  },

  createMaintenance: (token: string, id: number, body: CreateMaintenanceInput) =>
    request<void>(`/api/v1/assets/${id}/maintenances`, { method: 'POST', token, body }),

  listTyres: (token: string, id: number, signal?: AbortSignal) =>
    request<TyreRow[]>(`/api/v1/assets/${id}/tyres`, { token, signal }),

  createTyre: (token: string, id: number, body: CreateTyreInput) =>
    request<void>(`/api/v1/assets/${id}/tyres`, { method: 'POST', token, body }),

  removeTyre: (token: string, tyreId: number, body: RemoveTyreInput) =>
    request<void>(`/api/v1/assets/tyres/${tyreId}/remove`, { method: 'POST', token, body }),

  listInsurances: (token: string, id: number, signal?: AbortSignal) =>
    request<InsuranceRow[]>(`/api/v1/assets/${id}/insurances`, { token, signal }),

  createInsurance: (token: string, id: number, body: CreateInsuranceInput) =>
    request<void>(`/api/v1/assets/${id}/insurances`, { method: 'POST', token, body }),

  createLease: (token: string, id: number, body: CreateLeaseInput) =>
    request<void>(`/api/v1/assets/${id}/lease`, { method: 'POST', token, body }),

  addInstallment: (token: string, leaseId: number, body: AddInstallmentInput) =>
    request<void>(`/api/v1/assets/leases/${leaseId}/installments`, { method: 'POST', token, body }),

  /** Paie tout ou partie d'une échéance, depuis une banque ou une caisse : le serveur rend la pièce. */
  payInstallment: (token: string, installmentId: number, body: PayInstallmentInput) =>
    request<LeasePayment>(`/api/v1/assets/installments/${installmentId}/pay`, { method: 'POST', token, body }),

  /** Les paiements des échéances d'un contrat, annulés compris, les plus récents d'abord. */
  listLeasePayments: (token: string, leaseId: number, signal?: AbortSignal) =>
    request<LeasePayment[]>(`/api/v1/assets/leases/${leaseId}/payments`, { token, signal }),

  /** Le motif est obligatoire. L'échéance redevient due ; l'annulation ne se défait pas. */
  cancelLeasePayment: (token: string, paymentId: number, reason: string) =>
    request<LeasePayment>(`/api/v1/assets/lease-payments/${paymentId}/cancel`, { method: 'POST', token, body: { reason } }),

  createFixedCost: (token: string, id: number, body: CreateFixedCostInput) =>
    request<void>(`/api/v1/assets/${id}/fixed-costs`, { method: 'POST', token, body }),

  getHourlyCost: (token: string, id: number, fiscalYear?: number, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    if (fiscalYear) q.set('fiscalYear', String(fiscalYear))
    return request<HourlyCost>(`/api/v1/assets/${id}/hourly-cost?${q.toString()}`, { token, signal })
  },

  listStocktakes: (token: string, id: number, signal?: AbortSignal) =>
    request<StocktakeRow[]>(`/api/v1/assets/${id}/stocktakes`, { token, signal }),

  recordStocktake: (token: string, id: number, body: RecordStocktakeInput) =>
    request<void>(`/api/v1/assets/${id}/stocktakes`, { method: 'POST', token, body }),

  listAssetDocuments: (token: string, id: number, signal?: AbortSignal) =>
    request<AssetDocumentRow[]>(`/api/v1/assets/${id}/documents`, { token, signal }),

  addAssetDocument: (token: string, id: number, body: AddAssetDocumentInput) =>
    request<void>(`/api/v1/assets/${id}/documents`, { method: 'POST', token, body }),

  getAssetsDashboard: (token: string, fiscalYear?: number, signal?: AbortSignal) => {
    const q = new URLSearchParams()
    if (fiscalYear) q.set('fiscalYear', String(fiscalYear))
    return request<AssetsDashboard>(`/api/v1/assets-dashboard?${q.toString()}`, { token, signal })
  },

  // Actifs, lot G : tarif de référence, quatre heures et registre d'arrêts.
  //
  // `year` s'envoie explicitement plutôt que d'être laissé au serveur : la fiche lit
  // les trois routes pour le MÊME exercice, et laisser chacune retomber sur « l'année
  // en cours » ferait diverger les trois blocs pendant la nuit du 31 décembre.
  setAssetReferenceRate: (
    token: string,
    id: number,
    body: { source: string | null; year: number | null; rate: number | null },
  ) => request<void>(`/api/v1/assets/${id}/reference-rate`, { method: 'PUT', token, body }),

  getAssetHours: (token: string, id: number, year: number, signal?: AbortSignal) =>
    request<MachineHours>(`/api/v1/assets/${id}/hours?year=${String(year)}`, { token, signal }),

  listAssetDowntimes: (token: string, id: number, year: number, signal?: AbortSignal) =>
    request<DowntimeRow[]>(`/api/v1/assets/${id}/downtimes?year=${String(year)}`, { token, signal }),

  recordAssetDowntime: (
    token: string,
    id: number,
    body: { fromDate: string; toDate: string; hours: number; reason: string; notes?: string | null },
  ) => request<void>(`/api/v1/assets/${id}/downtimes`, { method: 'POST', token, body }),
}

// ---------------------------------------------------------------------------
// Types des ajouts ci-dessus
// ---------------------------------------------------------------------------

export interface ScopedFarm {
  farmId: number
  code: string
  name: string
  canWrite: boolean
}

export interface AssetCategory {
  id: number
  code: string
  name: string
  parentId: number | null
  isDepreciable: boolean
  defaultDepreciationYears: number | null
  isActive: boolean
}

export interface FixedAsset {
  id: number
  code: string
  designation: string
  categoryId: number | null
  brand: string | null
  model: string | null
  serialNumber: string | null
  registration: string | null
  manufactureYear: number | null
  farmId: number | null
  storageLocation: string | null
  responsibilityCenter: string | null
  invoiceNumber: string | null
  acquisitionDate: string | null
  commissioningDate: string | null
  entryValue: number
  status: string
  isDisposed: boolean
  qrPayload: string
  fuelTheoreticalLph: number | null
  /** Capacité annuelle déclarée (§15). Nulle = non déclarée, jamais zéro heure. */
  theoreticalHoursYear: number | null
  /** Tarif appliqué aux interventions quand la ligne n'en saisit aucun (§16). */
  referenceHourlyRate: number | null
  /** « budget » ou « fiscal_year » : un tarif sans origine est un chiffre en dur. */
  referenceRateSource: string | null
  referenceRateYear: number | null
}

export interface AssetTransfer {
  id: number
  fixedAssetId: number
  transferDate: string
  toLocation: string
  reason: string | null
}

export type CreateFixedAssetInput = {
  code?: string | null
  designation: string
  categoryId?: number | null
  brand?: string | null
  model?: string | null
  serialNumber?: string | null
  registration?: string | null
  manufactureYear?: number | null
  farmId?: number | null
  storageLocation?: string | null
  invoiceNumber?: string | null
  acquisitionDate?: string | null
  commissioningDate?: string | null
  entryValue: number
  putInService?: boolean
}

export type TransferFixedAssetInput = {
  toLocation: string
  toFarmId?: number | null
  reason?: string | null
  transferDate?: string | null
}

export type DisposeFixedAssetInput = {
  disposalMethod: string
  reason: string
  salePrice?: number | null
  buyerName?: string | null
  invoiceReference?: string | null
  disposalDate?: string | null
}

/**
 * La cession telle qu'elle se lit. `netBookValue` et `capitalGain` sont NULS
 * ENSEMBLE quand l'actif n'avait pas de plan d'amortissement, ou quand la cession
 * a été écrite avant la migration 0073. L'écran doit dire « non valorisée », jamais
 * afficher zéro : zéro se lirait comme « cédé sans perte ni gain », ce qui est une
 * information, alors que l'absence n'en est pas une.
 */
export type AssetDisposal = {
  id: number
  fixedAssetId: number
  disposalDate: string
  disposalMethod: string
  reason: string
  salePrice: number | null
  buyerName: string | null
  invoiceReference: string | null
  netBookValue: number | null
  capitalGain: number | null
}

export type InProgressExpense = {
  id: number
  expenseDate: string
  label: string
  amount: number
  supplierId: number | null
  supplierName: string | null
  invoiceReference: string | null
  notes: string | null
}

/**
 * `total` est la somme des lignes RENDUES, calculée par le serveur sur cette même
 * liste (ADR 0018) : le pied du tableau affiché et la valeur d'entrée que la mise en
 * service figera sont donc le même nombre. L'écran ne le recalcule pas.
 */
export type InProgressExpenses = {
  fixedAssetId: number
  status: string
  expenses: InProgressExpense[]
  total: number
}

export type AddInProgressExpenseInput = {
  expenseDate: string
  label: string
  amount: number
  supplierId?: number | null
  invoiceReference?: string | null
  notes?: string | null
}

export type CreateAssetCategoryInput = {
  code: string
  name: string
  parentId?: number | null
  isDepreciable?: boolean
  defaultDepreciationYears?: number | null
}

export interface MeterReading {
  id: number
  fixedAssetId: number
  readingDate: string
  meterKind: string
  meterValue: number
  source: string
}

export interface AssetUsage {
  operationMachineId: number
  operationId: number
  operationDate: string
  operationStatus: string
  machineId: number
  hours: number
  hourlyCost: number
  totalCost: number
}

export interface DepreciationLineRow {
  fiscalYear: number
  itemCode: string
  charge: number
  cumulative: number
  netBookValue: number
}

export interface DepreciationTable {
  planId: number
  durationYears: number
  startDate: string
  baseValue: number
  lines: DepreciationLineRow[]
}

export interface MaintenanceRow {
  id: number
  maintenanceDate: string
  kind: string
  title: string
  partsCost: number
  laborCost: number
  subcontractCost: number
  totalCost: number
  isClosed: boolean
  nextDueDate: string | null
}

export interface TyreRow {
  id: number
  position: string
  brand: string | null
  reference: string | null
  mountedOn: string
  mountedMeter: number | null
  removedOn: string | null
  price: number | null
}

export interface InsuranceRow {
  id: number
  insurer: string
  policyNumber: string
  startDate: string
  endDate: string
  premium: number
}

export interface StocktakeRow {
  id: number
  stocktakeDate: string
  existsPhysically: boolean
  locationOk: boolean
  condition: string
  notes: string | null
}

export interface AssetDocumentRow {
  id: number
  docType: string
  fileName: string
}

export interface ComponentRow {
  id: number
  code: string
  designation: string
  entryValue: number
  durationYears: number | null
}

export interface InstallmentRow {
  id: number
  dueDate: string
  principal: number
  interest: number
  vat: number
  paidAmount: number
  balance: number
}

export interface LeaseRow {
  id: number
  lessor: string
  contractRef: string
  startDate: string
  endDate: string
  assetValue: number
  downPayment: number
  installmentCount: number
  installmentAmount: number
  installments: InstallmentRow[]
}

export interface HourlyCost {
  fiscalYear: number
  fixedCosts: number
  variableCosts: number
  totalCost: number
  productiveHours: number
  hourlyCost: number
  depreciationCharge: number
  insuranceProrata: number
  leaseDue: number
  otherFixedCosts: number
  maintenanceTotal: number
  fuelCost: number
  fuelLiters: number
  tyreCost: number
  /** Au moins un pneu consommé n'a pas de prix : la part pneumatique est minorée. */
  tyreCostIncomplete: boolean
  maintenanceCostPerHour: number
  /** Nul quand aucune superficie n'est mesurée : « pas de donnée », pas zéro. */
  maintenanceCostPerHectare: number | null
}

/**
 * Les quatre heures d'un exercice et les deux taux (§15), tels que le domaine les
 * calcule. Un nul veut dire « la question n'a pas de réponse », jamais « zéro ».
 *
 * L'ASYMÉTRIE DES DEUX TAUX EST DÉLIBÉRÉE, et l'écran la reproduit sans la lisser :
 * sans capacité déclarée, les DEUX se taisent ; avec une capacité déclarée mais aucune
 * heure disponible, `utilizationRate` se tait — une machine immobilisée toute l'année
 * n'a pas un taux d'emploi infini — tandis que `availabilityRate` vaut alors zéro,
 * qui est une réponse.
 */
export interface MachineHours {
  fiscalYear: number
  theoreticalHours: number | null
  unavailableHours: number
  availableHours: number | null
  productiveHours: number
  utilizationRate: number | null
  availabilityRate: number | null
}

/**
 * Une ligne du registre d'indisponibilité, telle qu'elle a été SAISIE : `hours` est la
 * valeur écrite, jamais sa part proratée de l'exercice.
 *
 * `maintenanceOrderId` non nul désigne un arrêt DÉDUIT du carnet d'entretien : sa fin
 * est calculée à raison de vingt-quatre heures par jour civil, personne ne l'a tapée.
 */
export interface DowntimeRow {
  id: number
  fromDate: string
  toDate: string
  hours: number
  reason: string
  notes: string | null
  maintenanceOrderId: number | null
}

export interface AssetAlert {
  code: string
  label: string
  fixedAssetId: number | null
}

export interface AssetsDashboard {
  assetCount: number
  inServiceCount: number
  grossValue: number
  /**
   * NULS ENSEMBLE quand aucun actif du parc ne porte de plan d'amortissement.
   * Personne n'a encore décidé comment ce parc s'amortit : « cumul nul, VNC égale
   * au brut » se lirait comme un fait établi. La valeur brute, elle, ne se tait
   * jamais — c'est la somme de ce qu'on a payé, connue sans aucun plan.
   */
  accumulatedDepreciation: number | null
  netBookValue: number | null
  productiveHoursYear: number
  maintenanceCostYear: number
  fuelLitersYear: number
  missingInventoryCount: number
  /**
   * PLAFONNÉE PAR FAMILLE ET SANS TOTAL : le serveur coupe à vingt alertes par code de
   * coût et ne dit nulle part combien il en a écartées. L'écran ne doit donc pas
   * laisser croire qu'il les montre toutes.
   */
  alerts: AssetAlert[]
  /**
   * Coût horaire DU PARC, calculé sur les totaux et non en moyennant des ratios.
   * Nul quand aucun actif n'a de coût calculable : zéro ferait passer un parc sans
   * heures saisies pour un parc gratuit.
   */
  fleetHourlyCost: number | null
}

export type UpdateFixedAssetInput = {
  id?: number
  fuelTheoreticalLph?: number | null
  designation?: string | null
  categoryId?: number | null
  farmId?: number | null
  storageLocation?: string | null
  responsibilityCenter?: string | null
  entryValue?: number | null
  acquisitionDate?: string | null
  commissioningDate?: string | null
  putInService?: boolean
  putInProgress?: boolean
}

export type RecordMeterInput = {
  meterKind: string
  meterValue: number
  readingDate?: string | null
}

export type AttachAssetUsageInput = {
  operationId: number
  operationMachineId: number
  fixedAssetId: number
}

export type CreateDepreciationPlanInput = {
  durationYears: number
  startDate?: string | null
}

export type AddComponentInput = {
  code: string
  designation: string
  entryValue: number
  durationYears?: number | null
}

export type RevalueAssetInput = {
  newEntryValue: number
  reason: string
  revaluationDate?: string | null
}

export type CreateMaintenanceInput = {
  kind: string
  title: string
  maintenanceDate?: string | null
  partsCost?: number
  laborCost?: number
  subcontractCost?: number
  meterValue?: number | null
  downtimeHours?: number | null
  nextDueMeter?: number | null
  nextDueDate?: string | null
}

export type CreateTyreInput = {
  position: string
  brand?: string | null
  mountedMeter?: number | null
  price?: number | null
}

export type RemoveTyreInput = {
  removedOn: string
  removedMeter?: number | null
}

export type CreateInsuranceInput = {
  insurer: string
  policyNumber: string
  startDate: string
  endDate: string
  premium?: number
}

export type CreateLeaseInput = {
  lessor: string
  contractRef: string
  startDate: string
  endDate: string
  assetValue?: number
  downPayment?: number
  installmentCount?: number
  installmentAmount?: number
}

export type AddInstallmentInput = {
  dueDate: string
  principal: number
  interest: number
  vat?: number
}

export type PayInstallmentInput = {
  amount: number
  /** `null` : aujourd'hui, et c'est le serveur qui dit quel jour il est. */
  paidOn: string | null
  /** La banque ou la caisse d'où le loyer sort. `null` : le serveur le refuse. */
  treasuryAccountId: number | null
}

/** Le paiement d'une échéance de crédit-bail : une pièce, avec sa date, son montant et son compte. */
export interface LeasePayment {
  id: number
  installmentId: number
  installmentDueDate: string
  paymentDate: string
  amount: number
  /** Nul pour un paiement repris d'avant les comptes : il est « à affecter ». */
  treasuryAccountId: number | null
  treasuryAccountName: string | null
  status: 'posted' | 'cancelled'
  cancellationReason: string | null
  notes: string | null
}

export type CreateFixedCostInput = {
  fiscalYear: number
  label: string
  amount: number
}

export type RecordStocktakeInput = {
  stocktakeDate?: string | null
  existsPhysically?: boolean
  locationOk?: boolean
  condition?: string
  notes?: string | null
}

export type AddAssetDocumentInput = {
  docType: string
  fileName: string
  filePath: string
}

export interface UserDetail {
  id: number
  email: string
  fullName: string
  role: string
  locale: string
  isActive: boolean
  lastSignInAt: string | null
  /** Faux quand AUCUNE portée n'est posée : accès à tout, et non accès à rien. */
  hasFarmRestriction: boolean
  farms: ScopedFarm[]
  activityModules: string[]
  capabilities: string[]
}

export type UpdateUserInput = {
  fullName?: string | null
  role?: string | null
  locale?: string | null
  isActive?: boolean | null
}

export type SetUserScopesInput = {
  farmIds: number[]
  activityModuleCodes: string[]
}

export interface MyProfile {
  userId: number | null
  role: string | null
  locale: string
  capabilities: string[]
  allCapabilities: string[]
}

export interface UnassignedPlot {
  plotId: number
  code: string
  name: string | null
  farmId: number
  farmName: string
  cadastralAreaHectares: number
  isIrrigable: boolean
}

export type BulkAssignInput = {
  seasonId: number
  cropId: number
  plotIds: number[]
  varietyId?: number | null
  /** Nul : chaque parcelle est semée sur SA superficie cadastrale. */
  sownAreaHectares?: number | null
  sowingDate?: string | null
  expectedHarvestDate?: string | null
}

export interface BulkAssignOutcome {
  plotId: number
  plotCode: string | null
  succeeded: boolean
  sownAreaHectares: number | null
  errorCode: string | null
  errorMessage: string | null
}

export interface BulkAssignResult {
  requested: number
  succeeded: number
  failed: number
  totalAreaHectares: number
  outcomes: BulkAssignOutcome[]
}

export interface CustomFieldDefinition {
  id: number
  entityTable: string
  fieldKey: string
  name: string
  dataType: string
  isRequired: boolean
  defaultValue: string | null
  options: string[] | null
  validationRegex: string | null
  minValue: number | null
  maxValue: number | null
  displayOrder: number
}

export type CreateCustomFieldInput = {
  entityTable: string
  fieldKey: string
  name: string
  dataType: string
  isRequired?: boolean
  defaultValue?: string | null
  options?: string[] | null
  validationRegex?: string | null
  minValue?: number | null
  maxValue?: number | null
}

export interface CustomFieldValue {
  fieldKey: string
  name: string
  dataType: string
  isRequired: boolean
  options: string[] | null
  value: string | null
}

export interface SubsidyDto {
  id: number
  code: string
  name: string
  subsidyType: string
  grantedAmount: number
  status: string
  costObjectId: number | null
  farmId: number | null
  seasonId: number | null
  allocationCount: number
  allocatedAmount: number
}

export type CreateSubsidyInput = {
  code: string
  name: string
  subsidyType: string
  grantedAmount: number
  costObjectId?: number | null
  farmId?: number | null
  seasonId?: number | null
}

export type AllocateSubsidyInput = {
  basis: string
  costObjectIds?: number[] | null
}

export interface SubsidyAllocation {
  costObjectId: number
  plotCode: string | null
  eligibleAreaHectares: number
  allocatedAmount: number
}

export interface SubsidyAllocationResult {
  subsidyId: number
  basis: string
  grantedAmount: number
  allocatedTotal: number
  allocations: SubsidyAllocation[]
}

export type CheckWarningsInput = {
  plotSeasonId: number
  operationDate: string
  inputs: { productId: number; quantity: number; dosePerHectare?: number | null }[]
  workTypeId?: number | null
  coveredAreaHectares?: number | null
  operationCost?: number | null
  operationId?: number | null
  persist?: boolean
}

export interface OperationWarning {
  code: string
  message: string
  severity: string
  observedValue: number | null
  thresholdValue: number | null
  productId: number | null
}

export interface WarningsResponse {
  warnings: OperationWarning[]
  count: number
  plotCode: string
  areaHectares: number
  nitrogenAlreadyAppliedKg: number
  comparableAverageCostPerHectare: number | null
}

export interface FieldBookWarning {
  id: number
  code: string
  message: string
  severity: string
  observedValue: number | null
  thresholdValue: number | null
  acknowledged: boolean
}

export interface FieldBookEntry {
  date: string
  operationId: number | null
  documentNumber: string | null
  workTypeName: string | null
  products: string | null
  coveredAreaHectares: number | null
  warnings: FieldBookWarning[]
}

export interface HarvestValuation {
  plotSeasonId: number
  plotCode: string
  productName: string | null
  netHarvested: number
  soldQuantity: number
  unsoldQuantity: number
  averageSalePrice: number
  stockValue: number
  saleLineCount: number
}

export type CreateCreditNoteInput = {
  reason: string
  lines: { productId: number; quantity: number; unitPrice: number; stockLotId?: number | null }[]
  warehouseId?: number | null
  notes?: string | null
}

export interface SupplierCreditNoteResult {
  id: number
  documentNumber: string
  creditNoteOfId: number
  reason: string
  movedStock: boolean
  totalQuantity: number
  totalValue: number
}

export type AnalyseTakeoverInput = {
  kind: string
  fileName: string
  content: string
}

export interface TakeoverIssue {
  line: number
  code: string
  message: string
  value: string | null
}

export interface TakeoverRow {
  line: number
  fields: Record<string, string | null>
}

export interface TakeoverBatch {
  id: number
  kind: string
  fileName: string
  status: string
  rowCount: number
  loadedCount: number
  rejectedCount: number
  duplicateCount: number
  orphanCount: number
  issues: TakeoverIssue[]
  accepted: TakeoverRow[]
}


export interface StockBalanceRow {
  warehouseId: number
  productId: number
  quantity: number
  unitCost: number | null
  totalValue: number
}

export interface StockBalancesResult {
  items: StockBalanceRow[]
  totalValue: number
}

export interface MachineItem {
  id: number
  code: string
  name: string
  /** Coût horaire de référence du référentiel ; la ligne d'intervention le fige. */
  hourlyCost: number | null
}

/** Un salarié tel qu'on le désigne : sans aucun montant. */
export interface EmployeeItem {
  id: number
  code: string
  fullName: string
  jobTitle: string | null
  employmentTypeId: number
  employmentTypeName: string
  /** La base de paie de son type de personnel : hourly, daily, monthly, piece_rate. */
  payBasis: string
  farmId: number | null
  isActive: boolean
}

/** Un salarié avec ce qu'il coûte. Ne descend que chez qui voit les coûts. */
export interface EmployeeWithCosts extends EmployeeItem {
  hourlyCost: number | null
  dailyCost: number | null
  monthlyCost: number | null
}

export interface EmployeeInput {
  code: string
  fullName: string
  employmentTypeId: number
  jobTitle?: string | null
  farmId?: number | null
  hourlyCost?: number | null
  dailyCost?: number | null
  monthlyCost?: number | null
  isActive?: boolean
}

/** Une bête du registre d'élevage (lot LV-A) : son identité, pas son histoire. */
export interface AnimalItem {
  id: number
  farmId: number
  tagNumber: string
  speciesCode: string
  breedCode: string
  sex: string
  status: string
}

export interface AnimalInput {
  farmId: number
  tagNumber: string
  speciesCode: string
  breedCode: string
  sex: string
  birthDate?: string | null
  entryDate: string
  entryReason: string
  damId?: number | null
  sireId?: number | null
  categoryCode?: string | null
  notes?: string | null
}

export interface HerdMovementInput {
  direction: string
  movementDate: string
  reason: string
  /** Le prix de la transaction, quand il est connu : achat d'une entrée, vente d'une sortie. */
  priceTotal?: number | null
}

/** Une ligne d'inventaire : tant de têtes de cette espèce et catégorie. */
export interface HerdInventoryLine {
  speciesCode: string
  categoryCode: string | null
  animalCount: number
}

/** Une intervention sanitaire (lot LV-C) : son genre, sa date, son attente. */
export interface HealthEventItem {
  id: number
  animalId: number
  eventDate: string
  eventType: string
  productCode: string | null
  dose: number | null
  unitCode: string | null
  withdrawalDays: number
  veterinarianName: string | null
  /**
   * Le coût de l'intervention, quand il est saisi : part santé du coût par tête.
   * NUL pour qui ne voit pas les coûts — le serveur ne le rend pas (§4.4).
   */
  costTotal: number | null
  notes: string | null
}

export interface HealthEventInput {
  eventDate: string
  eventType: string
  productCode?: string | null
  dose?: number | null
  unitCode?: string | null
  withdrawalDays?: number | null
  veterinarianName?: string | null
  costTotal?: number | null
  notes?: string | null
}

/** Une bête dont la vaccination est due : la dernière remonte trop loin, ou n'existe pas. */
export interface VaccinationDueItem {
  animalId: number
  farmId: number
  tagNumber: string
  speciesCode: string
  lastVaccinationDate: string | null
}

/** Une traite (lot LV-D) : son jour, sa session, sa quantité. */
export interface ProductionItem {
  id: number
  animalId: number
  productionDate: string
  productType: string
  quantity: number
  unitCode: string
  qualityScore: number | null
  session: string | null
  collectionCenter: string | null
  notes: string | null
}

export interface ProductionInput {
  productionDate: string
  productType: string
  quantity: number
  unitCode?: string | null
  qualityScore?: number | null
  session?: string | null
  collectionCenter?: string | null
  notes?: string | null
}

/**
 * Une alimentation (lot LV-E) : son jour, son article, sa quantité. Les montants sont
 * NULS pour qui ne voit pas les coûts — le serveur ne les rend pas (§4.4).
 */
export interface FeedingRecordItem {
  id: number
  animalId: number
  feedingDate: string
  /** L'article sorti du stock (agro.product), désigné par son identifiant. */
  feedProductId: number
  quantityKg: number
  unitCost: number | null
  costTotal: number | null
  notes: string | null
}

export interface FeedingInput {
  feedingDate: string
  feedProductId: number
  quantityKg: number
  unitCost?: number | null
  costTotal?: number | null
  notes?: string | null
}

/** Une saillie (lot LV-F) : son jour, son type, sa date prévue. */
export interface ReproductionEventItem {
  id: number
  animalId: number
  eventDate: string
  eventType: string
  sireId: number
  /** Nulle quand l'espèce n'a pas de durée de gestation au référentiel. */
  expectedDueDate: string | null
  notes: string | null
}

export interface ReproductionInput {
  eventDate: string
  eventType?: string | null
  sireId: number
  notes?: string | null
}

/** Le coût cumulé d'une bête (lot LV-G) : achat + alimentation + santé, et sa marge si vendue. */
export interface LivestockAnimalCost {
  animalId: number
  tagNumber: string
  purchaseCost: number
  feedingCost: number
  healthCost: number
  totalCost: number
  salePrice: number
  /** Nulle pour une bête non vendue : « pas de donnée » n'est pas « marge nulle ». */
  margin: number | null
}

/** La synthèse des coûts : par tête et par litre. Les deux se taisent sans dénominateur. */
export interface LivestockCostReport {
  headCount: number
  totalCost: number
  liters: number
  costPerHead: number | null
  costPerLiter: number | null
  animals: LivestockAnimalCost[]
}

/** La campagne ouverte de la parcelle, porteuse de l'objet de coût. */
export interface PlotCostObject {
  costObjectId: number
  plotSeasonId: number
  seasonId: number | null
  plotCode: string
  plotName: string | null
  cropName: string | null
  sownAreaHectares: number
}

export interface StockedProduct {
  productId: number
  code: string
  name: string
  unitOfMeasureId: number
  quantity: number
}

export interface OperationTargetInput {
  costObjectId: number
  areaHectares?: number | null
  sharePct?: number | null
}

export interface OperationInput {
  farmId: number
  workTypeId: number
  operationDate?: string | null
  coveredAreaHectares?: number | null
  notes?: string | null
  targets?: OperationTargetInput[]
  /**
   * `hourlyCost` EST NULLABLE, ET C'EST TOUT L'OBJET DU LOT G. Un nul veut dire « je
   * n'ai pas saisi de tarif » et laisse le serveur résoudre celui de la fiche de
   * l'actif ; un zéro veut dire « je saisis zéro », ce qui est une intention — une
   * machine amortie et prêtée. Tant que ce champ était `number`, aucun client ne
   * pouvait exprimer le premier cas et la résolution du tarif de référence ne
   * s'exécutait jamais depuis le produit.
   */
  machines?: {
    machineId: number
    /**
     * ZÉRO VEUT DIRE « NON SAISI » QUAND UN COMPTEUR HORAIRE EST DONNÉ (lot 4a) : le
     * serveur prend alors l'écart du compteur. Une valeur saisie qui contredit le
     * compteur est refusée, jamais corrigée en silence.
     */
    hours: number
    hourlyCost: number | null
    fixedAssetId?: number | null
    /** `hours` : compteur horaire ; `km` : compteur kilométrique. Les trois vont ensemble. */
    meterKind?: 'hours' | 'km' | null
    meterStart?: number | null
    meterEnd?: number | null
    distanceKm?: number | null
    /** Le salarié qui conduisait. Ne coûte rien sur cette ligne. */
    operatorEmployeeId?: number | null
  }[]
  /**
   * Ce que l'intervention PRÉVOIT de consommer, dans l'unité de l'article, un article une
   * seule fois. Le réel ne se saisit pas ici : c'est une sortie de dépôt.
   */
  plannedInputs?: { productId: number; quantity: number }[]
  /**
   * Prestations sous-traitées (`service`, avec leur prestataire) et autres charges
   * (`other`). Le montant ne part pas : il EST quantité × prix, et le serveur le calcule.
   * `unitPrice` NUL veut dire « non saisi » et est refusé — zéro est un prix, vide non.
   */
  charges?: {
    kind: 'service' | 'other'
    description: string
    unitPrice: number | null
    quantity?: number
    partnerId?: number | null
  }[]
  labor?: {
    partnerId?: number | null
    employeeId?: number | null
    workerCount?: number
    quantity: number
    /**
     * NUL = « je n'ai pas saisi de coût » : le serveur applique celui de la fiche du
     * salarié selon la base de paie. C'est ce qui permet à un opérateur de désigner un
     * salarié sans que son salaire descende jamais sur son écran.
     */
    unitCost: number | null
    payBasis?: string
    /** `HH:mm:ss`. Les deux vont ensemble ; en base horaire, la quantité s'en déduit. */
    startTime?: string | null
    endTime?: string | null
  }[]
  /** Le responsable de l'intervention : un salarié du référentiel. */
  responsibleEmployeeId?: number | null
  validateImmediately?: boolean
}

export interface OperationSummary {
  id: number
  publicUid: string
  farmId: number
  workTypeId: number
  documentNumber: string | null
  operationDate: string
  status: string
  /**
   * LES TROIS MONTANTS SONT NULS POUR QUI NE VOIT PAS LES COÛTS (lot 4a). Le serveur les
   * retient — il ne les met pas à zéro : depuis que le coût d'une ligne de main-d'œuvre
   * interne se lit sur la fiche du salarié, rendre le total d'une intervention à qui la
   * saisit, c'était lui rendre le salaire d'un collègue.
   */
  inputCost: number | null
  machineAndLaborCost: number | null
  totalCost: number | null
}

/**
 * Le prévu et le réel d'un article sur une intervention. `plannedQuantity` et `gapQuantity`
 * sont NULS pour un article sorti sans avoir été prévu — il n'y a rien à quoi le comparer.
 * `actualQuantity` vaut zéro tant que rien n'est sorti : c'est un fait, pas une absence.
 */
export interface OperationInputPlanLine {
  productId: number
  productCode: string
  productName: string
  unitCode: string
  plannedQuantity: number | null
  actualQuantity: number
  gapQuantity: number | null
}

export interface ConsumptionResultLine {
  productId: number
  stockMovementId: number
  quantity: number
  unitPrice: number
  totalCost: number
}

export interface ConsumeForOperationResult {
  operationId: number
  warehouseId: number
  totalCost: number
  lines: ConsumptionResultLine[]
}

export interface CostBreakdownLine {
  label: string
  quantity: number
  unitPrice: number
  totalCost: number
}

export interface CostBreakdownSection {
  section: string
  totalCost: number
  lines: CostBreakdownLine[]
}

export interface OperationCostBreakdown {
  operationId: number
  inputCost: number
  machineCost: number
  laborCost: number
  /** Prestations sous-traitées et autres charges : la colonne « Autres ». Comptée dans `directCost`. */
  otherCost: number
  directCost: number
  sections: CostBreakdownSection[]
}

/** Une ligne refusée à l'import : son numéro dans le fichier et un code stable traduisable. */
export interface ImportErrorRow {
  line: number
  code: string
  message: string
}

export interface ImportReport {
  totalRows: number
  createdCount: number
  failedCount: number
  errors: ImportErrorRow[]
  createdIds: number[]
}

/**
 * La premiere parcelle, creee dans le meme mouvement que son exploitation.
 *
 * Sans elle, `CreateFarmHandler` fait naitre l'exploitation INACTIVE : la
 * migration 0050 refuse au commit une exploitation active sans parcelle active.
 * C'est un brouillon legitime, mais il faut que l'ecran le dise.
 */
export type FirstPlotInput = {
  code: string
  name?: string | null
  cadastralAreaHectares: number
  latitude?: number | null
  longitude?: number | null
  isIrrigable?: boolean
}

export type FarmInput = {
  code?: string | null
  name: string
  governorate?: string | null
  delegation?: string | null
  locality?: string | null
  managerName?: string | null
  phone?: string | null
  totalAreaHectares?: number | null
  latitude?: number | null
  longitude?: number | null
  description?: string | null
  ownerName?: string | null
  address?: string | null
  firstPlot?: FirstPlotInput | null
  farmTypeId?: number | null
}

export type PlotInput = {
  farmId: number
  code: string
  name?: string | null
  cadastralAreaHectares: number
  soilDepthCm?: number | null
  averageSlopePercent?: number | null
  latitude?: number | null
  longitude?: number | null
  isIrrigable?: boolean
  parentPlotId?: number | null
  validFrom?: string | null
  validTo?: string | null
  soilTypeId?: number | null
  irrigationModeId?: number | null
  waterSourceId?: number | null
}

export type OrchardPlotInput = {
  farmId: number
  plotId: number
  cropId: number
  varietyId?: number | null
  plantingYear: number
  plantingMonth?: number | null
  densityPerHa?: number | null
  region?: string | null
  plantationCostTotal?: number | null
  usefulLifeYears?: number | null
  notes?: string | null
}

export interface OrchardIntervention {
  id: number
  farmId: number
  orchardPlotId: number
  seasonId: number | null
  interventionDate: string
  interventionTypeId: number
  productId: number | null
  stockMovementId: number | null
  dosePerHa: number | null
  areaHa: number | null
  laborHours: number | null
  laborCost: number | null
  status: string
  notes: string | null
}

export type OrchardInterventionInput = {
  farmId: number
  orchardPlotId: number
  seasonId?: number | null
  interventionDate: string
  interventionTypeId: number
  productId?: number | null
  warehouseId?: number | null
  quantity?: number | null
  dosePerHa?: number | null
  areaHa?: number | null
  laborHours?: number | null
  laborCost?: number | null
  notes?: string | null
}

/** Un choix de formulaire du verger : l'identifiant qu'on envoie, le libellé qu'on lit. */
export interface OrchardChoice {
  id: number
  code: string
  label: string
  farmId: number | null
  orchardPlotId: number | null
}

/** Tout ce que les formulaires du verger proposent de choisir (GET /api/v1/orchard/catalog). */
export interface OrchardCatalog {
  farms: OrchardChoice[]
  landPlots: OrchardChoice[]
  orchardPlots: OrchardChoice[]
  crops: OrchardChoice[]
  interventionTypes: OrchardChoice[]
  seasons: OrchardChoice[]
  waterSources: OrchardChoice[]
  warehouses: OrchardChoice[]
  harvestPasses: OrchardChoice[]
}

export interface OrchardHarvestPass {
  id: number
  farmId: number
  orchardPlotId: number
  seasonId: number
  passNumber: number
  harvestDate: string
  grossWeightKg: number
  tareWeightKg: number
  netWeightKg: number
  qualityGrade: string | null
  workerCount: number | null
  laborCost: number | null
  notes: string | null
}

export type OrchardHarvestPassInput = {
  farmId: number
  orchardPlotId: number
  seasonId: number
  passNumber: number
  harvestDate: string
  grossWeightKg: number
  tareWeightKg: number
  workerCount?: number | null
  laborCost?: number | null
  qualityGrade?: string | null
  notes?: string | null
}

export interface OrchardQualityControl {
  id: number
  farmId: number
  harvestPassId: number
  controlDate: string
  acidityPct: number | null
  peroxideIndex: number | null
  brixDegrees: number | null
  caliber: string | null
  defectPct: number | null
  oilYieldPct: number | null
  classification: string | null
  notes: string | null
}

export type OrchardQualityControlInput = {
  farmId: number
  harvestPassId: number
  controlDate: string
  acidityPct?: number | null
  peroxideIndex?: number | null
  brixDegrees?: number | null
  caliber?: string | null
  defectPct?: number | null
  oilYieldPct?: number | null
  classification?: string | null
  notes?: string | null
}

export interface OrchardIrrigation {
  id: number
  farmId: number
  orchardPlotId: number
  irrigationDate: string
  volumeM3: number
  waterSourceId: number
  costPerM3: number | null
  costTotal: number | null
  stockMovementId: number | null
  notes: string | null
}

export type OrchardIrrigationInput = {
  farmId: number
  orchardPlotId: number
  irrigationDate: string
  volumeM3: number
  waterSourceId: number
  costPerM3?: number | null
  stockMovementId?: number | null
  notes?: string | null
}

export interface OrchardTransformation {
  id: number
  farmId: number
  harvestPassId: number
  millName: string | null
  processingDate: string
  inputKg: number
  outputKg: number
  yieldPct: number
  oilAcidityPct: number | null
  oilClassification: string | null
  batchCode: string | null
  notes: string | null
}

export type OrchardTransformationInput = {
  farmId: number
  harvestPassId: number
  processingDate: string
  inputKg: number
  outputKg: number
  oilAcidityPct?: number | null
  millName?: string | null
  batchCode?: string | null
  notes?: string | null
}

export interface OrchardPhenology {
  id: number
  farmId: number
  orchardPlotId: number
  observationDate: string
  bbchStage: string
  stageLabel: string | null
  notes: string | null
}

export type OrchardPhenologyInput = {
  farmId: number
  orchardPlotId: number
  bbchStage: string
  observationDate?: string | null
  stageLabel?: string | null
  notes?: string | null
}

export interface Season {
  id: number
  code: string
  name: string
  startDate: string
  endDate: string
  isCurrent: boolean
  isClosed: boolean
}

/**
 * Ce qu'une facture émise doit encore : facturé − avoirs − encaissé. `remainingAmount` peut
 * être NÉGATIF — un avoir émis après le paiement — et c'est alors l'entreprise qui doit.
 * `dueDate` nulle : sans échéance convenue, ce qui n'est pas « échue ».
 */
export interface InvoiceBalance {
  invoiceId: number
  partnerId: number
  partnerName: string
  documentNumber: string
  invoiceDate: string
  dueDate: string | null
  invoicedAmount: number
  creditedAmount: number
  paidAmount: number
  remainingAmount: number
}

export interface CustomerPaymentMethod {
  id: number
  code: string
  name: string
  /** Un chèque, un virement, une traite se désignent ; des espèces non. */
  requiresReference: boolean
  /** Ce que le mode fait bouger : une banque, une caisse, ou rien (échange en nature, vente à terme). */
  treasuryKind: TreasuryKind
}

export interface CustomerPayment {
  id: number
  documentNumber: string
  partnerId: number
  partnerName: string
  paymentDate: string
  paymentMethodId: number
  methodCode: string
  reference: string | null
  amount: number
  allocatedAmount: number
  /** Encaissé, pas encore imputé : une avance. Zéro pour un encaissement annulé. */
  unallocatedAmount: number
  status: 'posted' | 'cancelled'
  cancellationReason: string | null
  /** Le compte où l'argent est entré ; nul pour un mode qui ne fait rien bouger, ou un encaissement d'avant les comptes. */
  treasuryAccountId: number | null
  treasuryAccountName: string | null
}

export type CustomerPaymentAllocation = { invoiceId: number; amount: number }

export type CustomerPaymentInput = {
  partnerId: number
  amount: number
  paymentMethodId: number
  paymentDate?: string | null
  reference?: string | null
  notes?: string | null
  allocations: CustomerPaymentAllocation[]
  treasuryAccountId: number | null
}

export type ReceivableBucketKey = 'not_due' | '1-30' | '31-60' | '61-90' | '90+' | 'no_due_date'

/** Ce qu'un client doit, par ancienneté. `net` retire ses avances et ce qu'on lui doit. */
export interface PartnerReceivable {
  partnerId: number
  partnerName: string
  openInvoiceCount: number
  outstanding: number
  buckets: { key: ReceivableBucketKey; amount: number }[]
  unallocated: number
  overpaid: number
  net: number
}

export interface Receivables {
  asOf: string
  partners: PartnerReceivable[]
  /** Le pied : la somme des clients rendus, calculée par le serveur. */
  totals: PartnerReceivable
}

export interface PartnerReceivableDetail {
  partnerId: number
  invoices: InvoiceBalance[]
  advances: CustomerPayment[]
}

/** Les quatre natures de coût que le réalisé connaît. */
export type BudgetCostNature = 'input' | 'machine' | 'labor' | 'other'

export interface BudgetLine {
  id: number
  nature: BudgetCostNature
  label: string
  quantityPerHa: number
  unitOfMeasureId: number | null
  unitCode: string | null
  unitPrice: number
  /** Quantité × prix, calculé par la base. Au millime. */
  costPerHa: number
}

/**
 * Une culture de la campagne et son budget. `areaHa` et `plotSeasonCount` viennent de
 * l'assolement : ils ne se saisissent pas. `costPerHa` nul veut dire « pas budgétée »,
 * zéro « budgétée, sans ligne encore ».
 */
export interface CropBudget {
  cropId: number
  cropName: string
  plotSeasonCount: number
  areaHa: number
  isBudgeted: boolean
  expectedYieldPerHa: number | null
  yieldUnitId: number | null
  yieldUnitCode: string | null
  forecastUnitPrice: number | null
  notes: string | null
  lines: BudgetLine[]
  costPerHa: number | null
}

export interface SeasonBudget {
  seasonId: number
  isClosed: boolean
  crops: CropBudget[]
}

/** Tout est facultatif — mais un rendement ou un prix se donne avec son unité. */
export type CropBudgetAssumptionsInput = {
  expectedYieldPerHa: number | null
  yieldUnitId: number | null
  forecastUnitPrice: number | null
  notes?: string | null
}

/** `unitPrice` nul : le champ est resté vide. Le serveur le refuse — zéro est un prix, vide non. */
export type BudgetLineInput = {
  nature: string
  label: string
  quantityPerHa: number
  unitPrice: number | null
  unitOfMeasureId: number | null
}

export type ComparisonPostKey =
  | 'input'
  | 'machine'
  | 'labor'
  | 'other'
  | 'direct_cost'
  | 'yield'
  | 'yield_per_ha'
  | 'revenue'
  | 'gross_margin'

/** L'écart est réalisé − prévu. Tout ce qui manque est nul, jamais zéro. */
export interface ComparisonPost {
  key: ComparisonPostKey
  budget: number | null
  actual: number | null
  gap: number | null
  gapPercent: number | null
}

export interface CropComparison {
  cropId: number
  cropName: string
  plotSeasonCount: number
  areaHa: number
  yieldUnitCode: string | null
  forecastUnitPrice: number | null
  /** Une récolte est pesée dans une autre unité que celle du budget : la production réalisée se tait. */
  harvestUnitMismatch: boolean
  posts: ComparisonPost[]
}

export interface UnbudgetedCrop {
  cropId: number
  cropName: string
  plotSeasonCount: number
  areaHa: number
  actualDirectCost: number
  actualRevenue: number
}

export interface BudgetComparison {
  seasonId: number
  crops: CropComparison[]
  /** Le pied : la somme des cultures BUDGÉTÉES, et d'elles seules. */
  totals: ComparisonPost[]
  unbudgeted: UnbudgetedCrop[]
}

export interface PlotSeason {
  id: number
  publicUid: string
  plotId: number
  plotCode: string
  plotName: string | null
  seasonId: number
  seasonCode: string
  cropId: number
  cropName: string | null
  varietyId: number | null
  varietyName: string | null
  sownAreaHectares: number
  sowingDate: string | null
  expectedHarvestDate: string | null
  status: string
  costObjectId: number
  /**
   * Identité propre de la campagne (§12, migration 0050) : `agro.plot_season` EST la
   * campagne, ces colonnes ne sont donc pas un objet séparé. `code` est toujours servi
   * (auto-dérivé côté serveur quand l'écran ne le saisit pas) ; le reste peut manquer
   * sur une campagne créée avant ce lot.
   */
  code: string | null
  name: string | null
  campaignGroupId: number | null
  campaignGroupName: string | null
  description: string | null
  startDate: string | null
  endDate: string | null
}

/**
 * Une norme (§8) : une comparaison évaluable, jamais une phrase. `expression` est
 * composée par la base (colonne générée, migration 0051) et n'apparaît dans aucun
 * formulaire — elle ne se lit qu'en retour.
 */
export interface Norm {
  id: number
  normCategoryId: number
  normCategoryName: string
  code: string
  name: string
  description: string | null
  parameterCode: string
  normKind: 'minimum' | 'maximum' | 'range' | 'exact' | 'qualitative'
  minValue: number | null
  minInclusive: boolean
  maxValue: number | null
  maxInclusive: boolean
  exactValue: number | null
  tolerance: number | null
  unitOfMeasureId: number | null
  unitSymbol: string | null
  expression: string
  displayOrder: number
  isActive: boolean
}

/**
 * Ce que le formulaire envoie pour créer une norme. Pas de champ `expression` : la
 * spécification demande explicitement d'éviter une expression en texte saisie, et la
 * base la refuserait de toute façon puisque la colonne est générée.
 *
 * `inclusive` est UN SEUL booléen, partagé par les deux bornes d'une plage : aucune
 * norme agronomique ne s'écrit avec une borne stricte et l'autre large.
 */
export type CreateNormInput = {
  normCategoryId: number
  code: string
  name: string
  description?: string | null
  parameterCode: string
  normKind: Norm['normKind']
  minValue?: number | null
  maxValue?: number | null
  exactValue?: number | null
  tolerance?: number | null
  inclusive?: boolean
  unitOfMeasureId?: number | null
}

/** Une norme appliquée à une campagne (§13). `verdict` est calculé par la base : aucun formulaire ne le porte. */
export interface PlotSeasonNorm {
  id: number
  plotSeasonId: number
  normId: number
  normCode: string
  normName: string
  normKind: Norm['normKind']
  expression: string
  unitSymbol: string | null
  appliedOn: string
  observedValue: number | null
  verdict: 'compliant' | 'non_compliant' | 'not_measured' | 'not_applicable'
  comment: string | null
}

/**
 * Ce que l'écran envoie pour appliquer une norme à une campagne.
 *
 * `verdict` n'a d'effet que pour une norme QUALITATIVE — la seule qu'aucun nombre ne
 * tranche, où le jugement vient de l'observateur. Envoyé pour toute autre forme, il
 * est écrasé par le déclencheur de la base ; l'écran ne le propose donc que dans ce cas.
 */
export type ApplyNormInput = {
  normId: number
  observedValue?: number | null
  appliedOn?: string | null
  comment?: string | null
  verdict?: PlotSeasonNorm['verdict'] | null
}

export type AssignCropInput = {
  plotId: number
  seasonId: number
  cropId: number
  sownAreaHectares: number
  varietyId?: number | null
  cropSequence?: number | null
  sowingDate?: string | null
  expectedHarvestDate?: string | null
  code?: string | null
  name?: string | null
  campaignGroupId?: number | null
  description?: string | null
  startDate?: string | null
  endDate?: string | null
}

export interface YieldMetrics {
  plotSeasonId: number
  costObjectId: number
  plotId: number
  plotCode: string
  plotName: string | null
  seasonId: number
  seasonCode: string
  cropName: string | null
  sownAreaHectares: number
  grossQuantity: number
  netQuantity: number
  normalizedQuantity: number
  yieldPerHectareBrut: number | null
  yieldPerHectareNet: number | null
  yieldPerHectareNormalized: number | null
  farmAverageNormalizedPerHa: number | null
  seasonAverageNormalizedPerHa: number | null
  deviationFromFarmAvgPct: number | null
  deviationFromSeasonAvgPct: number | null
}

export interface UnitCosts {
  costObjectId: number
  netCost: number
  areaHectares: number | null
  yieldQuintals: number | null
  costPerQuintal: number | null
  costPerHectare: number | null
}

export interface CostAggregateRow {
  level: string
  keyId: number
  label: string
  objectCount: number
  areaHa: number | null
  inputCost: number
  machineCost: number
  laborCost: number
  otherCost: number
  directCost: number
  subsidyAmount: number
  netCost: number
  revenue: number
  grossMargin: number
  margin: number
  costPerHa: number | null
  marginPerHa: number | null
  yieldQuantity: number | null
}

export interface CostAggregateDto {
  level: string
  rows: CostAggregateRow[]
  totals: {
    objectCount: number
    areaHa: number
    directCost: number
    subsidyAmount: number
    netCost: number
    revenue: number
    grossMargin: number
    margin: number
    costPerHa: number | null
  }
}

export interface PurchaseOrderLine {
  id: number
  productId: number
  productCode: string | null
  productName: string | null
  unitOfMeasureId: number
  quantity: number
  unitPrice: number
  discountPct: number | null
  // Mêmes noms que PurchaseOrderLineDto côté API : l'écran lisait quantityReceived /
  // quantityRemaining / lineTotal et affichait « undefined » (recette du 2026-09-03).
  receivedQuantity: number
  remainingQuantity: number
  lineExcludingTax: number
}

export interface PurchaseOrder {
  id: number
  publicUid: string
  documentNumber: string | null
  partnerId: number
  partnerName: string | null
  status: string
  orderDate: string
  expectedDate: string | null
  totalExcludingTax: number
  totalIncludingTax: number
  notes: string | null
  lines: PurchaseOrderLine[]
  isEditable: boolean
}

export type PurchaseRequisitionStatus = 'submitted' | 'approved' | 'rejected' | 'cancelled'

/** Ce que les commandes non annulées couvrent d'une demande. Calculé par le serveur, jamais par l'écran. */
export type RequisitionFulfilment = 'none' | 'partial' | 'full'

export interface PurchaseRequisitionListItem {
  id: number
  documentNumber: string
  requestDate: string
  neededBy: string | null
  farmId: number | null
  farmName: string | null
  status: PurchaseRequisitionStatus
  notes: string | null
  lineCount: number
  fulfilment: RequisitionFulfilment
}

/**
 * Une ligne de demande, avec ce qui en est commandé. `orderedQuantity` vaut zéro tant que
 * rien n'est commandé — un fait, pas une absence — et `remainingQuantity` ne devient jamais
 * négatif : on commande au conditionnement, parfois plus que demandé.
 */
export interface PurchaseRequisitionLine {
  requisitionLineId: number
  lineNumber: number
  productId: number
  productCode: string
  productName: string
  unitOfMeasureId: number
  unitCode: string
  requestedQuantity: number
  orderedQuantity: number
  remainingQuantity: number
  notes: string | null
}

export interface PurchaseRequisition {
  id: number
  documentNumber: string
  requestDate: string
  neededBy: string | null
  farmId: number | null
  warehouseId: number | null
  status: PurchaseRequisitionStatus
  notes: string | null
  requestedBy: number | null
  decidedAt: string | null
  decidedBy: number | null
  decisionReason: string | null
  fulfilment: RequisitionFulfilment
  lines: PurchaseRequisitionLine[]
  /**
   * Celui qui lit peut-il RETIRER cette demande ? Le serveur le dit : il faut en être le
   * demandeur, qu'elle ne soit pas décidée, et pouvoir saisir le travail. L'écran ne rejoue
   * pas la règle — sa session ne sait même pas qui il est.
   */
  canWithdraw: boolean
}

export type PurchaseRequisitionInput = {
  lines: { productId: number; quantity: number; notes?: string | null }[]
  farmId?: number | null
  warehouseId?: number | null
  neededBy?: string | null
  notes?: string | null
}

export type PurchaseOrderInput = {
  partnerId: number
  lines: {
    productId: number
    unitOfMeasureId: number
    quantity: number
    unitPrice: number
    discountPct?: number | null
    /**
     * La ligne de demande d'achat à laquelle cette ligne répond. La demande doit être
     * approuvée, l'article le même, et l'unité celle de l'article — le serveur le refuse sinon.
     */
    requisitionLineId?: number | null
  }[]
  orderDate?: string | null
  expectedDate?: string | null
  farmId?: number | null
  warehouseId?: number | null
  paymentTermId?: number | null
  notes?: string | null
  documentNumber?: string | null
  approveImmediately?: boolean
}

export type ReceiveGoodsInput = {
  warehouseId: number
  receiptDate?: string | null
  supplierDocument?: string | null
  notes?: string | null
  lines: {
    orderLineId: number
    quantityArrived: number
    rejectedQuantity?: number | null
    rejectionReason?: string | null
    /** Lu sur l'emballage. Ne sert que pour un article suivi par lot. */
    supplierLotCode?: string | null
    expiryDate?: string | null
  }[]
}

// Ce que la route rend réellement (PurchaseReceiptDto). L'ancienne forme décrivait des
// champs que le serveur n'envoie pas ; personne ne lisait la réponse, donc rien ne tombait.
export interface PurchaseReceipt {
  receiptNumber: string
  receiptDate: string
  orderId: number
  orderStatus: string
  warehouseId: number
  totalValue: number
  lines: {
    orderLineId: number
    productId: number
    arrivedQuantity: number
    acceptedQuantity: number
    rejectedQuantity: number
    rejectionReason: string | null
    unitCost: number
    lineValue: number
    stockMovementId: number | null
    /** Le lot créé par cette ligne, pour un article suivi par lot. Il naît en contrôle. */
    lotId: number | null
    lotCode: string | null
  }[]
}

export interface ProductDto {
  id: number
  code: string
  name: string
  productTypeId: number
  productTypeCode: string
  unitOfMeasureId: number
  unitCode: string
  valuationMethod: string
  standardCost: number | null
  isStockManaged: boolean
  isLotTracked: boolean
  barcode: string | null
}

export type CreateProductInput = {
  code: string
  name: string
  productTypeId: number
  unitOfMeasureId: number
  valuationMethod?: string | null
  standardCost?: number | null
  isStockManaged?: boolean
  isLotTracked?: boolean
  barcode?: string | null
}

export interface ProductPriceDto {
  id: number
  productId: number
  partnerId: number | null
  partnerName: string | null
  priceKind: string
  unitPrice: number
  minQuantity: number
  validFrom: string
  validTo: string | null
}

export type CreateProductPriceInput = {
  partnerId?: number | null
  priceKind: string
  unitPrice: number
  minQuantity?: number | null
  validFrom?: string | null
  validTo?: string | null
}

export interface HarvestBatchDto {
  id: number
  plotSeasonId: number
  productId: number
  harvestDate: string
  grossQuantity: number
  netQuantity: number
  moisturePct: number | null
  impurityPct: number | null
  normalizedQuantity: number
  /** Entrée au journal du stock : lot, solde, mouvement. Faux : pesée, pas encore mise en stock. */
  isInStock: boolean
  /** Faux sur une récolte en stock : l'article n'avait pas de coût standard, elle est entrée en quantité seule. */
  isValued: boolean
  /** Récolte antérieure à la mise en stock des récoltes : livrable sans stock, jamais mise en stock. */
  stockExempt: boolean
  lotCode: string | null
  qualityStatus: LotQualityStatus | null
  warehouseCode: string | null
}

export type CreateHarvestBatchInput = {
  plotSeasonId: number
  productId: number
  unitOfMeasureId: number
  grossQuantity: number
  netQuantity: number
  harvestDate?: string | null
  moisturePct?: number | null
  impurityPct?: number | null
  qualityGrade?: string | null
  harvestedAreaHectares?: number | null
  notes?: string | null
  /** Facultatif : avec dépôt, la récolte est mise en stock dans le même geste. */
  warehouseId?: number | null
}

/** Facture OU avoir : la nature les distingue, et l avoir porte des totaux négatifs. */
export interface SupplierInvoiceDocument {
  id: number
  kind: 'invoice' | 'credit_note'
  documentNumber: string
  partnerId: number
  partnerName: string
  invoiceDate: string
  totalExcludingTax: number
  totalIncludingTax: number
  paidAmount: number
  status: string
  creditNoteOfId: number | null
  creditReason: string | null
}

export interface ThreeWayMatchLine {
  productId: number
  productCode: string
  productName: string
  orderedQuantity: number
  receivedQuantity: number
  invoicedQuantity: number
  orderedValue: number
  receivedValue: number
  invoicedValue: number
}

/**
 * Le rapprochement d'une facture. EN ATTENTE, ses écarts sont ceux que l'approbation verra,
 * calculés par le serveur ; DÉCIDÉ, ceux que la décision a écrits.
 */
export interface ThreeWayMatch {
  invoiceId: number
  status: 'pending' | 'matched' | 'variance_accepted' | 'rejected'
  quantityVariance: number
  valueVariance: number
  varianceReason: string | null
  lines: ThreeWayMatchLine[]
  /** Tout concorde, ligne par ligne : l'approbation ne demandera pas de motif. */
  isClean: boolean
  /** Le code de ce qui empêche de confronter la facture — elle ne peut alors qu'être refusée. */
  obstacle: string | null
}

/** Ce qu'une décision a fait de la facture : une dette (`approved`) ou un litige (`disputed`). */
export interface SupplierInvoiceDecision {
  invoiceId: number
  invoiceStatus: 'approved' | 'disputed'
  matchStatus: 'matched' | 'variance_accepted' | 'rejected'
  quantityVariance: number
  valueVariance: number
  varianceReason: string | null
  dueDate: string | null
}

export interface SupplierPaymentMethod {
  code: string
  /** Un chèque et une traite ont une échéance : c'est ce jour-là que l'argent sort. */
  requiresDueDate: boolean
  /** Ce que le mode fait bouger : une banque, une caisse, ou rien (compensation). */
  treasuryKind: TreasuryKind
}

export interface SupplierPayment {
  id: number
  documentNumber: string
  partnerId: number
  partnerName: string
  paymentDate: string
  method: string
  reference: string | null
  /** L'échéance du chèque ou de la traite ; nulle pour les autres modes. */
  dueDate: string | null
  amount: number
  allocatedAmount: number
  /** Versé, pas encore imputé : une avance. Zéro pour un règlement annulé. */
  unallocatedAmount: number
  status: 'issued' | 'presented' | 'cleared' | 'bounced' | 'cancelled'
  cancellationReason: string | null
  /** Le compte d'où l'argent sort ; nul pour une compensation, ou un règlement d'avant les comptes. */
  treasuryAccountId: number | null
  treasuryAccountName: string | null
}

export type SupplierPaymentInput = {
  partnerId: number
  amount: number
  method: string
  paymentDate?: string | null
  reference?: string | null
  dueDate?: string | null
  notes?: string | null
  allocations: CustomerPaymentAllocation[]
  treasuryAccountId: number | null
}

/** Ce qu'un mode de règlement fait bouger. `none` : rien — il ne désigne aucun compte. */
export type TreasuryKind = 'bank' | 'cash' | 'none'

/** Un compte de banque ou une caisse, et son solde : l'ouverture, plus les entrées, moins les sorties. */
export interface TreasuryAccount {
  id: number
  code: string
  name: string
  kind: 'bank' | 'cash'
  bankReference: string | null
  isActive: boolean
  openingDate: string
  openingBalance: number
  inflow: number
  outflow: number
  balance: number
}

export interface TreasurySituation {
  accounts: TreasuryAccount[]
  /** Le pied : la somme des comptes rendus, calculée par le serveur. */
  totals: { bank: number; cash: number; total: number }
}

/** Un compte tel qu'un formulaire de paiement le propose : sans aucun montant. */
export interface TreasuryAccountChoice {
  id: number
  code: string
  name: string
  kind: 'bank' | 'cash'
  openingDate: string
}

export type TreasuryAccountInput = {
  code: string
  name: string
  kind: string
  /** `null` : non saisi — le serveur le refuse. Un compte vide s'ouvre à 0, écrit. */
  openingBalance: number | null
  openingDate: string | null
  bankReference: string | null
}

export interface StatementLine {
  date: string
  direction: 'in' | 'out'
  amount: number
  sourceKind: 'customer_payment' | 'supplier_payment' | 'lease_payment' | 'treasury_entry'
  sourceId: number
  /** Le tiers — ou, pour un mouvement saisi, son libellé ; pour un virement, le compte d'en face. */
  counterparty: string
  reference: string | null
  /** Le solde du compte une fois ce mouvement passé. */
  balanceAfter: number
}

export interface AccountStatement {
  account: TreasuryAccount
  from: string | null
  to: string | null
  /** Ce que le compte valait la veille de la période — son ouverture, sans période. */
  openingBalance: number
  lines: StatementLine[]
  inflow: number
  outflow: number
  closingBalance: number
}

/** Un engagement donné — salaire, loyer, assurance, impôt, autre charge — et ce qu'il doit encore. */
export interface Commitment {
  id: number
  category: string
  label: string
  counterparty: string | null
  dueDate: string
  amount: number
  /** La somme des mouvements de trésorerie enregistrés qui le désignent. */
  paidAmount: number
  /** Zéro pour un engagement annulé. */
  remainingAmount: number
  status: 'open' | 'cancelled'
  settlement: 'unpaid' | 'partial' | 'settled' | 'cancelled'
  cancellationReason: string | null
}

export interface Commitments {
  due: Commitment[]
  /** Le pied : la somme de ce qui reste des engagements dus, calculée par le serveur. */
  totalDue: number
  closed: Commitment[]
}

export interface CommitmentOptions {
  categories: string[]
  intervals: string[]
  maxCount: number
}

export type CommitmentInput = {
  category: string
  label: string
  amount: number
  /** `null` : non saisie — le serveur la refuse. On n'invente pas de date à un engagement. */
  firstDueDate: string | null
  counterparty: string | null
  /** `null` : un seul engagement. */
  count: number | null
  /** Le pas d'une série ; `null` pour un engagement seul. */
  interval: string | null
}

export type CommitmentPaymentInput = {
  accountId: number | null
  /** `null` : tout ce qui reste dû. */
  amount: number | null
  paidOn: string | null
  reference: string | null
}

export interface TreasuryEntryCategory {
  code: string
  /** Un virement sort d'un compte et entre dans un autre : il demande un second compte, pas un sens. */
  isTransfer: boolean
}

/** Un mouvement saisi. Pour un virement, il sort de `accountId` et entre dans `counterAccountId`. */
export interface TreasuryEntry {
  id: number
  accountId: number
  accountName: string
  counterAccountId: number | null
  counterAccountName: string | null
  entryDate: string
  direction: 'in' | 'out'
  category: string
  label: string
  reference: string | null
  amount: number
  status: 'posted' | 'cancelled'
  cancellationReason: string | null
}

export type TreasuryEntryInput = {
  accountId: number
  category: string
  amount: number
  label: string
  /** `null` pour un virement : il sort toujours du compte d'origine. */
  direction: 'in' | 'out' | null
  /** Le compte de destination d'un virement ; `null` pour le reste. */
  counterAccountId: number | null
  entryDate: string | null
  reference: string | null
}

/** Un paiement saisi avant les comptes : il fait bouger de l'argent et ne dit pas où. */
export interface UnassignedPayment {
  sourceKind: 'customer_payment' | 'supplier_payment' | 'lease_payment'
  sourceId: number
  paymentDate: string
  direction: 'in' | 'out'
  amount: number
  counterparty: string
  reference: string
  /** `any` : une banque ou une caisse, l'une ou l'autre — un paiement d'échéance n'a pas de mode. */
  expectedKind: 'bank' | 'cash' | 'any'
}

export interface PartnerPayableDetail {
  partnerId: number
  invoices: InvoiceBalance[]
  advances: SupplierPayment[]
}

export type ScheduleBucketKey = 'overdue' | '0-7' | '8-30' | '31-60' | '61-90' | '90+' | 'no_due_date'

/** Une tranche de l'échéancier — ou son pied, de clé `total`. */
export interface ScheduleBucket {
  key: ScheduleBucketKey | 'total'
  receivable: number
  payable: number
  net: number
  /**
   * Ce que la trésorerie vaudrait à la FIN de la tranche si chacun payait à sa date. Nul quand il
   * n'y a pas de prévision, pour ce qui n'a pas d'échéance, et pour le pied.
   */
  projectedBalance: number | null
}

/**
 * Une échéance : ce qui reste à recevoir (`in`) ou à payer (`out`), à sa date. `dueDate`
 * nulle : une facture sans échéance. `daysOverdue` nul : elle n'est pas échue.
 */
export interface ScheduleEntry {
  direction: 'in' | 'out'
  kind:
    | 'customer_invoice'
    | 'supplier_invoice'
    | 'lease_installment'
    | 'supplier_instrument'
    | 'commitment_salary'
    | 'commitment_rent'
    | 'commitment_insurance'
    | 'commitment_tax'
    | 'commitment_other'
  sourceId: number
  partnerId: number | null
  counterparty: string
  reference: string
  dueDate: string | null
  amount: number
  bucket: ScheduleBucketKey
  daysOverdue: number | null
}

export interface PaymentSchedule {
  asOf: string
  buckets: ScheduleBucket[]
  /** Le pied : la somme des tranches, calculée par le serveur. */
  totals: ScheduleBucket
  entries: ScheduleEntry[]
  /**
   * Ce qu'on a aujourd'hui en banque et en caisse : le départ de la prévision. Nul quand on regarde
   * un autre jour qu'aujourd'hui, ou qu'aucun compte n'est ouvert — il n'y a alors pas de prévision.
   */
  treasury: number | null
}

/** Ce qu'une campagne a produit d'un article, et son rendement sur la surface récoltée — nul sans surface. */
export interface ProductionLine {
  productCode: string
  productName: string
  unitCode: string
  quantity: number
  areaHa: number
  yieldPerHa: number | null
}

/** Les indicateurs du dirigeant. Un indicateur qui n'a pas de quoi se calculer est `null`, jamais zéro. */
export interface ExecutiveDashboard {
  asOf: string
  /** La campagne lue : celle demandée, à défaut celle en cours. `null` s'il n'y en a pas. */
  season: { id: number; code: string; name: string } | null
  farmedAreaHa: number | null
  cultivatedAreaHa: number | null
  costPerHa: number | null
  machineCostPerHa: number | null
  marginPerHa: number | null
  production: ProductionLine[]
  fuelLiters: number | null
  fuelCostPerHa: number | null
  stockInputValue: number
  stockProductValue: number
  receivables: number
  payables: number
  /** `null` quand aucun compte n'est ouvert. */
  treasury: number | null
}

/** Une ligne d'alerte. Ce qui n'a pas de sens pour sa famille est nul — jamais zéro. */
export interface AlertLine {
  /** Le code de la famille — ou, pour une alerte agronomique, celui de la règle dépassée. */
  code: string
  subject: string
  reference: string | null
  /** Le dépôt, pour une alerte de stock. */
  location: string | null
  date: string | null
  daysOverdue: number | null
  quantity: number | null
  unitCode: string | null
  threshold: number | null
  amount: number | null
  entityKind: string
  entityId: number
}

/** Une famille d'alertes : `count` les compte toutes, `lines` n'en montre que les premières. */
export interface AlertFamily {
  code: string
  count: number
  /** Sur toutes les alertes de la famille, coupées ou non ; nul pour une famille sans montant. */
  totalAmount: number | null
  lines: AlertLine[]
}

export interface AlertCenter {
  asOf: string
  total: number
  families: AlertFamily[]
}

/** Un mouvement d'un compte : un paiement non annulé, vu depuis son compte. */
export interface TreasuryMovement {
  movementDate: string
  direction: 'in' | 'out'
  amount: number
  /** Le montant dans le sens du compte : positif s'il entre, négatif s'il sort. */
  signedAmount: number
  sourceKind: 'customer_payment' | 'supplier_payment' | 'lease_payment' | 'treasury_entry'
  sourceId: number
  /** Le tiers — ou, pour un mouvement saisi, son libellé ; pour un virement, le compte d'en face. */
  counterparty: string
  reference: string | null
}

/** Un relevé bancaire et où il en est : tout est calculé par le serveur. */
export interface BankStatement {
  id: number
  accountId: number
  accountCode: string
  accountName: string
  statementDate: string
  reference: string | null
  openingBalance: number
  closingBalance: number
  pointedCount: number
  pointedAmount: number
  /** Le solde initial plus ce qui est pointé. */
  pointedBalance: number
  /** Le solde final, moins le solde initial, moins ce qui est pointé. */
  gap: number
  isReconciled: boolean
  /** Ce qui sépare le solde initial du solde final du relevé précédent ; nul pour le premier relevé d'un compte. */
  continuityGap: number | null
  isContinuous: boolean
}

export interface BankReconciliation {
  statement: BankStatement
  pointed: TreasuryMovement[]
  unpointed: TreasuryMovement[]
}

export type BankStatementInput = {
  statementDate: string | null
  /** `null` : non saisi — le serveur le refuse. Un compte vide se relève à 0, écrit. */
  openingBalance: number | null
  closingBalance: number | null
  reference: string | null
}

export type CreateSupplierInvoiceInput = {
  partnerId: number
  purchaseOrderId: number
  lines: { productId: number; quantity: number; unitPrice: number }[]
  supplierReference?: string | null
  taxAmount?: number
  /** La date que porte la facture du fournisseur. Absente : aujourd'hui. */
  invoiceDate?: string | null
  dueDate?: string | null
}

/** Ce qui se facture encore d'un article sur une commande : la quantité reçue et pas facturée, au prix de la réception. */
export interface InvoicableLine {
  productId: number
  productCode: string
  productName: string
  quantity: number
  unitPrice: number
}

export interface InvoicableOrder {
  purchaseOrderId: number
  partnerId: number
  orderNumber: string
  orderDate: string
  lines: InvoicableLine[]
}

export type CreateLandedCostInput = {
  chargeTypeId: number
  goodsReceiptId: number
  amount: number
  allocationBasis: string
  documentNumber?: string | null
  notes?: string | null
}

export interface LandedCostDto {
  id: number
  chargeTypeId: number
  chargeTypeCode: string
  amount: number
  allocationBasis: string
  isAllocated: boolean
  goodsReceiptId: number | null
}

export interface LandedCostAllocationDto {
  id: number
  goodsReceiptLineId: number
  basisValue: number
  allocatedAmount: number
}

export interface UserDto {
  id: number
  email: string
  fullName: string
  role: string
  isActive: boolean
}

export type CreateUserInput = {
  email: string
  fullName: string
  role: string
  password: string
}



export interface CustomerInvoiceSummary {
  id: number
  kind: 'invoice' | 'credit_note'
  documentNumber: string
  invoiceDate: string
  partnerId: number
  partnerName: string
  creditNoteOfId: number | null
  reason: string | null
  totalExcludingTax: number
  totalIncludingTax: number
  status: string
}

/** Un bon de livraison qui reste à facturer. `totalExcludingTax` est la somme de ses lignes rendues, calculée par le serveur. */
export interface InvoicableDelivery {
  deliveryNoteId: number
  partnerId: number
  documentNumber: string | null
  deliveryDate: string
  totalExcludingTax: number
  lines: { productCode: string; productName: string; quantity: number; unitPrice: number; excludingTax: number }[]
}

export interface InvoiceTaxRate {
  id: number
  code: string
  name: string
  ratePct: number
}

export type CreateCustomerInvoiceInput = {
  partnerId: number
  deliveryNoteIds: number[]
  invoiceDate?: string | null
  /** Nul : aucun taux n'est appliqué par-dessus celui de la livraison. */
  taxRateId?: number | null
  notes?: string | null
  dueDate?: string | null
}

export type CustomerCreditNoteInput = {
  reason: string
  lines: { invoiceLineId: number; quantity: number }[]
  notes?: string | null
}

export interface AuditEntry {
  id: number
  occurredAt: string
  userId: number | null
  userName: string | null
  action: 'insert' | 'update' | 'delete'
  entitySchema: string
  entityTable: string
  entityId: number | null
  changedColumns: string[]
  oldValues: string | null
  newValues: string | null
  requestId: string | null
}

export type AuditSearchParams = {
  entityTable?: string
  entityId?: number
  userId?: number
  action?: string
  /** Sans dates, le serveur retient le mois écoulé : la table est partitionnée par mois. */
  from?: string
  to?: string
  page?: number
  size?: number
}

export interface SeasonLifecycle {
  id: number
  code: string
  name: string
  startDate: string
  endDate: string
  isCurrent: boolean
  isClosed: boolean
  closedAt: string | null
  reopeningCount: number
}

export type OpenSeasonInput = {
  code: string
  name: string
  startDate: string
  endDate: string
  moduleCode?: string | null
}

/** Ce qui empêche une clôture, par motif et par nombre (§10.5). */
export interface ClosingBlocker {
  code: string
  count: number
}

export interface StockAlert {
  code: string
  severity: string
  productId: number
  productCode: string
  productName: string
  warehouseId: number | null
  warehouseCode: string | null
  lotId: number | null
  lotNumber: string | null
  quantity: number
  threshold: number | null
  expiryDate: string | null
  daysRemaining: number | null
}

/** Une ligne du journal des mouvements, remontée jusqu'à son document d'origine (L5-11). */
export interface StockMovementRow {
  id: number
  movementDate: string
  direction: string
  reasonCode: string
  reasonName: string
  warehouseId: number
  warehouseCode: string
  productId: number
  productCode: string
  productName: string
  lotId: number | null
  lotCode: string | null
  quantity: number
  unitValue: number
  totalValue: number
  /** `operation`, `goods_receipt`, `delivery_note`, `harvest_batch` — ou nul pour une régularisation. */
  sourceKind: string | null
  sourceReference: string | null
  sourceId: number | null
}

export interface StockLotRow {
  lotId: number
  lotCode: string
  productId: number
  productCode: string
  productName: string
  warehouseId: number
  warehouseCode: string
  expiryDate: string | null
  daysToExpiry: number | null
  remainingQuantity: number
  totalValue: number
  /** in_control / released / blocked / rejected / conditional : en contrôle, bloqué et rejeté empêchent la sortie. */
  qualityStatus: string
  /** Code du lot dont cette part a été détachée (migration 0087), s'il y en a un. */
  parentLotCode: string | null
  /** Grade de la part — « A », « B ». Une étiquette : il ne valorise ni ne juge rien. */
  qualityGrade: string | null
}

/** D'où vient un lot, par ce qui l'a écrit. « unknown » : rien ne le dit, et c'est une réponse. */
export type LotOriginKind = 'split' | 'purchase' | 'harvest' | 'takeover' | 'direct' | 'unknown'

/** Un lot, tel que la traçabilité le nomme. */
export interface TracedLot {
  lotId: number
  lotCode: string
  productCode: string
  productName: string
  unitCode: string
  supplierLotCode: string | null
  expiryDate: string | null
  receivedAt: string
  initialQuantity: number
  qualityStatus: string
  qualityGrade: string | null
  originKind: LotOriginKind
}

/**
 * La traçabilité d'un lot (migration 0089). L'amont répond de ce dont le lot vient ;
 * l'aval, de ce qui en est issu — ses parts comprises.
 */
export interface LotTrace {
  lot: TracedLot
  /** L'origine de la marchandise : pour une part, celle de son plus ancien lot d'origine. */
  origin: {
    kind: LotOriginKind
    isKnown: boolean
    lotId: number
    lotCode: string
    supplierName: string | null
    receiptNumber: string | null
    receiptDate: string | null
    supplierDocument: string | null
    orderNumber: string | null
    plotCode: string | null
    plotName: string | null
    seasonCode: string | null
    cropName: string | null
    harvestDate: string | null
    takeoverCause: string | null
    takeoverDate: string | null
    directEntryDate: string | null
  }
  /** Du plus proche au plus ancien. */
  ancestors: TracedLot[]
  controls: { lotId: number; lotCode: string; controlDate: string; basis: string; result: string; decision: string }[]
  /** Ce qu'on a mis dans le champ d'une récolte, avant elle. Lien par la parcelle, pas par la matière. */
  fieldInputs: {
    operationNumber: string | null
    operationDate: string
    workType: string | null
    productCode: string
    productName: string
    lotId: number | null
    lotCode: string | null
    supplierName: string | null
    quantity: number
  }[]
  parts: TracedLot[]
  balances: { lotId: number; lotCode: string; warehouseCode: string; warehouseName: string; quantity: number }[]
  movements: { lotId: number; lotCode: string; movementId: number; movementDate: string; direction: string; reasonCode: string; warehouseCode: string; quantity: number }[]
  consumptions: {
    lotId: number
    lotCode: string
    movementId: number
    operationId: number
    operationNumber: string | null
    operationDate: string
    workType: string | null
    plotCodes: string | null
    quantity: number
    isCancelled: boolean
  }[]
  /** Les récoltes des parcelles où le lot a été appliqué avant elles. */
  harvests: {
    harvestBatchId: number
    lotId: number | null
    lotCode: string | null
    harvestDate: string
    quantity: number
    productCode: string
    productName: string
    plotCode: string
    plotName: string | null
    seasonCode: string
  }[]
  deliveries: {
    lotId: number
    lotCode: string
    movementId: number
    deliveryNoteId: number
    deliveryNumber: string | null
    deliveryDate: string | null
    customerName: string | null
    quantity: number
  }[]
}

/** Une part à détacher d'un lot : sa quantité, et un grade facultatif. */
export type LotSplitInput = {
  warehouseId: number
  parts: { quantity: number; qualityGrade?: string | null }[]
  reason?: string | null
}

/** Ce que rend une scission : ce que le lot d'origine garde, et les parts avec leur statut HÉRITÉ. */
export interface LotSplit {
  splitId: number
  parentLotId: number
  parentLotCode: string
  remainingQuantity: number
  children: { lotId: number; lotCode: string; quantity: number; qualityGrade: string | null; qualityStatus: string }[]
}

/** Statuts qualité d'un lot (§17). `downgraded` est hérité : il n'est plus proposé. */
export type LotQualityStatus = 'in_control' | 'released' | 'blocked' | 'rejected' | 'conditional' | 'downgraded'

/** `not_measured` n'est pas un défaut : il appelle une mesure, il ne clôt rien. */
export type MeasureVerdict = 'compliant' | 'non_compliant' | 'not_measured'

/** Une mesure telle que la base l'a jugée : le seuil FIGÉ ce jour-là, le verdict posé par le déclencheur. */
export interface QualityMeasure {
  parameterId: number
  normId: number
  normName: string
  expression: string
  unitSymbol: string | null
  controlType: string
  isMandatory: boolean
  observedValue: number | null
  verdict: MeasureVerdict
  comment: string | null
}

export interface QualityControl {
  id: number
  lotId: number
  lotCode: string
  productId: number
  productName: string
  warehouseId: number | null
  warehouseCode: string | null
  controlDate: string
  /** Renseigné sur les seuls contrôles antérieurs à 0085. */
  controlType: string | null
  /** measured : jugé contre la norme. no_standard : décision humaine. override : dérogation. inherited : hérité du lot d'origine. legacy : ancien. */
  basis: 'measured' | 'no_standard' | 'override' | 'inherited' | 'takeover' | 'legacy'
  /** Posé par la base, jamais saisi. */
  result: 'compliant' | 'non_compliant' | 'incomplete' | 'no_standard' | 'waived'
  decision: LotQualityStatus
  notes: string | null
  sampleQuantity: number | null
  decidedAt: string | null
  decidedBy: number | null
  standardCode: string | null
  standardVersion: string | null
  overrideReason: string | null
  measures: QualityMeasure[]
}

export interface ControlSheetParameter {
  parameterId: number
  normId: number
  normName: string
  normKind: Norm['normKind']
  expression: string
  unitSymbol: string | null
  isMandatory: boolean
  controlType: string
}

/** `standardId` nul : l'article n'a pas de norme en vigueur, il n'y a rien à mesurer. */
export interface ControlSheet {
  lotId: number
  lotCode: string
  productId: number
  productName: string
  qualityStatus: LotQualityStatus
  on: string
  standardId: number | null
  standardCode: string | null
  standardVersion: string | null
  parameters: ControlSheetParameter[]
}

/**
 * Il n'y a NI `result` NI `decision` : c'est tout l'objet du lot Q1a. `verdict` ne sert
 * qu'aux paramètres qualitatifs, où l'observateur juge ; ailleurs la base l'ignore.
 */
export type MeasuredControlInput = {
  lotId: number
  measures: { parameterId: number; observedValue?: number | null; verdict?: MeasureVerdict | null; comment?: string | null }[]
  notes?: string | null
  sampleQuantity?: number | null
  controlDate?: string | null
  warehouseId?: number | null
}

export type ManualControlInput = {
  lotId: number
  decision: 'released' | 'blocked' | 'rejected'
  notes?: string | null
}

export type QualityOverrideInput = {
  lotId: number
  decision: 'conditional' | 'rejected'
  reason: string
  notes?: string | null
}

export interface ProductStandardParameter {
  id: number
  normId: number
  normCode: string
  normName: string
  normKind: Norm['normKind']
  expression: string
  unitSymbol: string | null
  isMandatory: boolean
  controlType: string
  displayOrder: number
}

/** Une norme de produit. `parameters` est vide dans une liste : la grille se lit sur la fiche. */
export interface ProductStandard {
  id: number
  productId: number
  productCode: string
  productName: string
  code: string
  name: string
  version: string
  effectiveFrom: string
  reference: string | null
  status: 'draft' | 'active' | 'retired'
  parameterCount: number
  mandatoryCount: number
  parameters: ProductStandardParameter[]
}

export type ProductStandardInput = {
  productId: number
  code: string
  name: string
  version: string
  effectiveFrom: string
  reference?: string | null
  parameters: { normId: number; isMandatory: boolean; controlType: string }[]
}

/** Une ligne de feuille d'inventaire : le théorique seul, la colonne « compté » reste vide. */
export interface StockCountRow {
  warehouseId: number
  warehouseCode: string
  productId: number
  productCode: string
  productName: string
  unitCode: string
  lotId: number | null
  lotCode: string | null
  theoreticalQuantity: number
  unitCost: number
}

export interface ContractProgress {
  id: number
  documentNumber: string
  partnerId: number
  partnerName: string
  seasonCode: string | null
  contractDate: string
  validTo: string | null
  status: string
  contractedQuantity: number
  deliveredQuantity: number
  remainingQuantity: number
  totalExcludingTax: number
  deliveryCount: number
}

export type SalesContractInput = {
  partnerId: number
  /** Facultatif : omis, le serveur l'alloue dans la suite des contrats. */
  documentNumber?: string | null
  seasonId?: number | null
  validTo?: string | null
  lines: { productId: number; unitOfMeasureId: number; quantity: number; unitPrice: number }[]
}

export interface SalesContractLine {
  id: number
  lineNumber: number
  productId: number
  unitOfMeasureId: number
  quantity: number
  deliveredQuantity: number
  remainingQuantity: number
  unitPrice: number
}

export interface SalesContract {
  id: number
  documentNumber: string
  partnerId: number
  seasonId: number | null
  contractDate: string
  validTo: string | null
  status: string
  totalExcludingTax: number
  lines: SalesContractLine[]
}

/** Ni prix ni article : ils viennent du contrat. Le lot, lui, vient de celui qui charge le camion. */
export type DeliveryInput = {
  warehouseId: number
  deliveryDate?: string | null
  carrier?: string | null
  vehiclePlate?: string | null
  lines: {
    salesContractLineId: number
    quantity: number
    /** Obligatoire pour un article suivi par lot : c'est ce lot qui sort du stock. */
    lotId?: number | null
  }[]
}

export interface DeliveryNote {
  id: number
  documentNumber: string
  salesContractId: number | null
  warehouseId: number
  deliveryDate: string
  status: string
  totalExcludingTax: number
  lines: {
    id: number
    lineNumber: number
    salesContractLineId: number | null
    productId: number
    quantity: number
    unitPrice: number
    lineExcludingTax: number
    lotId: number | null
    /** La sortie de stock de la ligne. Nulle pour un article hors stock. */
    stockMovementId: number | null
  }[]
}

/**
 * L'encours facturé d'un client. PAS DE CHAMP « réglé » : le schéma ne tient aucun
 * règlement client, et une colonne à zéro se lirait « il n'a jamais payé » au lieu de
 * « le logiciel ne le sait pas ».
 */
export interface CustomerOutstanding {
  partnerId: number
  partnerName: string
  invoiced: number
  outstanding: number
  openInvoiceCount: number
}

export interface NearbyPlot {
  plotId: number
  code: string
  name: string | null
  farmId: number
  farmName: string
  areaHectares: number
  distanceMeters: number
}
